import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  Activity,
  ArrowRight,
  Building2,
  CalendarDays,
  ChevronRight,
  CheckCircle2,
  ClipboardCheck,
  Eye,
  EyeOff,
  Flag,
  Handshake,
  MapPin,
  Plus,
  Search,
  ShieldCheck,
  Target,
  Trophy,
  UserPlus,
  Users,
} from 'lucide-react';
import SEO from '../../components/SEO/SEO';
import { useAuth } from '../../contexts/AuthContext';
import { locationApi } from '../../services/location.service';
import sportsService, {
  CreateChallengePayload,
  CreateGroundPayload,
  CreateTeamPayload,
  CreateTournamentPayload,
  ExperienceLevel,
  ExistingSportsPlayer,
  GenderCategory,
  ManualTeamPlayerPayload,
  RegisterPlayerPayload,
  SportCategoryName,
  TournamentFormat,
  VerificationRequirement,
} from '../../services/sports.service';

const SITE_URL = 'https://localally.in';
const SPORTS_HERO_IMAGE =
  'https://images.unsplash.com/photo-1540747913346-19e32dc3e97e?auto=format&fit=crop&w=1800&q=80';

const sportOptions: SportCategoryName[] = [
  'CRICKET',
  'VOLLEYBALL',
  'FOOTBALL',
  'KABADDI',
  'BADMINTON',
  'RUNNING',
  'TENNIS',
  'OTHER',
];
const playingRoleOptionsBySport: Record<SportCategoryName, string[]> = {
  CRICKET: ['Batter', 'Bowler', 'All Rounder', 'Wicket Keeper', 'Captain'],
  VOLLEYBALL: ['Setter', 'Outside Hitter', 'Opposite Hitter', 'Middle Blocker', 'Libero', 'Defensive Specialist', 'All Rounder'],
  FOOTBALL: ['Goalkeeper', 'Defender', 'Full Back', 'Midfielder', 'Winger', 'Forward', 'Striker'],
  KABADDI: ['Raider', 'Defender', 'All Rounder', 'Left Corner', 'Right Corner', 'Left Cover', 'Right Cover'],
  BADMINTON: ['Singles Player', 'Doubles Player', 'Mixed Doubles Player', 'Attacker', 'Defensive Player', 'All Rounder'],
  RUNNING: ['Sprinter', 'Middle Distance Runner', 'Long Distance Runner', 'Marathon Runner', 'Relay Runner'],
  TENNIS: ['Singles Player', 'Doubles Player', 'Baseline Player', 'Serve And Volley Player', 'All Court Player'],
  OTHER: ['Player', 'Captain', 'All Rounder', 'Goalkeeper', 'Defender', 'Attacker'],
};
const levelOptions: ExperienceLevel[] = ['BEGINNER', 'LOCAL', 'INTERMEDIATE', 'ADVANCED'];
const genderOptions: GenderCategory[] = ['MALE', 'FEMALE', 'OTHER'];
const tournamentGenderOptions: GenderCategory[] = ['MALE', 'FEMALE', 'MIXED', 'OPEN'];
const tournamentFormatOptions: TournamentFormat[] = [
  'KNOCKOUT',
  'LEAGUE',
  'ROUND_ROBIN',
  'SEMI_FINAL_FINAL',
  'BEST_OF_3',
  'FRIENDLY_SERIES',
  'ONE_DAY_TOURNAMENT',
  'WEEKEND_TOURNAMENT',
];
const verificationOptions: VerificationRequirement[] = [
  'UNVERIFIED_PLAYERS_ALLOWED',
  'CAPTAIN_ONLY_VERIFICATION',
  'MINIMUM_VERIFIED_PLAYERS_REQUIRED',
  'ALL_PLAYERS_VERIFIED_REQUIRED',
];

type MessageState = {
  type: 'success' | 'error';
  text: string;
} | null;

const getRecordId = (item: any) => {
  if (!item) return '';
  if (typeof item.id === 'string') return item.id;
  if (typeof item._id === 'string') return item._id;
  if (typeof item._id?.$oid === 'string') return item._id.$oid;
  if (typeof item._id?.toString === 'function') return item._id.toString();
  return '';
};

const formatEnum = (value?: string) =>
  String(value || '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const getPlayingRoleOptions = (sport?: string) =>
  playingRoleOptionsBySport[(sport as SportCategoryName) || 'OTHER'] || playingRoleOptionsBySport.OTHER;

const parseNumber = (value: string) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && value.trim() ? parsed : undefined;
};

const resolveApiErrorMessage = (error: any, fallback: string): string => {
  const payload = error?.response?.data;
  const candidate = payload?.message || payload?.error || error?.message;
  if (typeof candidate === 'string' && candidate.trim()) return candidate;
  if (Array.isArray(candidate)) return candidate.join(' ');
  return fallback;
};

const isAccountConflictError = (error: any) => {
  const payload = error?.response?.data;
  const message = payload?.message?.message || payload?.message || error?.message || '';
  return error?.response?.status === 409 || String(message).toLowerCase().includes('already in use');
};

const normalizeSportsPhone = (phone: string) =>
  phone.trim().startsWith('+') ? `+${phone.replace(/\D/g, '')}` : phone.trim();

type SeoSchema = object | object[];

const stripEmptySchemaValues = (record: Record<string, any>) =>
  Object.fromEntries(
    Object.entries(record).filter(([, value]) => {
      if (value === undefined || value === null || value === '') return false;
      if (Array.isArray(value)) return value.length > 0;
      return true;
    }),
  );

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'LocalAlly',
  url: SITE_URL,
  logo: `${SITE_URL}/logo_1.jpeg`,
};

const makeBreadcrumbSchema = (items: Array<{ name: string; path: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: `${SITE_URL}${item.path}`,
  })),
});

const makeSportsServiceSchema = ({
  name,
  description,
  path,
  serviceType = 'Local sports team, tournament, ground and player discovery',
}: {
  name: string;
  description: string;
  path: string;
  serviceType?: string;
}) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name,
  serviceType,
  provider: {
    '@type': 'Organization',
    name: 'LocalAlly',
    url: SITE_URL,
  },
  areaServed: [
    { '@type': 'AdministrativeArea', name: 'Andhra Pradesh' },
    { '@type': 'AdministrativeArea', name: 'Telangana' },
    { '@type': 'Country', name: 'India' },
  ],
  url: `${SITE_URL}${path}`,
  description,
});

const makeSportsPageSchemas = (title: string, description: string, path: string, serviceType?: string): object[] => [
  organizationSchema,
  makeSportsServiceSchema({ name: `${title} | LocalAlly Sports Connect`, description, path, serviceType }),
  makeBreadcrumbSchema([
    { name: 'LocalAlly', path: '/' },
    { name: 'Sports Connect', path: '/sports' },
    ...(path === '/sports' ? [] : [{ name: title, path }]),
  ]),
];

const pageSchema = makeSportsPageSchemas(
  'LocalAlly Sports Connect',
  'Connect local players, captains, teams, tournaments, grounds and sponsors for village and town sports communities.',
  '/sports',
);

const sportsAppLinks = [
  { to: '/sports/dashboard', label: 'Dashboard', icon: Activity },
  { to: '/sports/register', label: 'Register', icon: UserPlus },
  { to: '/sports/create-team', label: 'Teams', icon: Users },
  { to: '/sports/tournaments', label: 'Tournaments', icon: Trophy },
  { to: '/sports/challenge-match', label: 'Challenge', icon: Target },
  { to: '/sports/grounds', label: 'Grounds', icon: Building2 },
];

const sectionClass = (maxWidth = 'max-w-7xl', extra = '') =>
  ['mx-auto', maxWidth, 'px-4 py-6 sm:px-6 sm:py-8 lg:px-8 lg:py-10', extra].filter(Boolean).join(' ');

const panelClass = (extra = '') =>
  [
    'rounded-lg border border-slate-200 bg-white/95 p-4 shadow-[0_18px_48px_-36px_rgba(15,23,42,0.28)] sm:p-6',
    extra,
  ]
    .filter(Boolean)
    .join(' ');

const PageShell: React.FC<{
  title: string;
  description: string;
  path: string;
  schema?: SeoSchema;
  robots?: string;
  image?: string;
  seoTitle?: string;
  children: React.ReactNode;
}> = ({ title, description, path, schema, robots, image = SPORTS_HERO_IMAGE, seoTitle, children }) => {
  const location = useLocation();
  const isActiveLink = (to: string, label: string) =>
    location.pathname === to ||
    location.pathname.startsWith(`${to}/`) ||
    (label === 'Register' && location.pathname.startsWith('/sports/register'));

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,_#f8fafc_0%,_#eefdf5_42%,_#f8fafc_100%)] text-slate-950">
      <SEO
        title={seoTitle || `${title} | LocalAlly`}
        description={description}
        path={path}
        image={image}
        robots={robots}
        schema={schema || makeSportsPageSchemas(title, description, path)}
      />
      <section
        className="relative overflow-hidden border-b border-emerald-900/20 bg-slate-950 text-white"
        style={{ backgroundImage: `linear-gradient(90deg, rgba(2,6,23,0.94), rgba(6,78,59,0.78), rgba(234,88,12,0.34)), url(${SPORTS_HERO_IMAGE})`, backgroundPosition: 'center', backgroundSize: 'cover' }}
      >
        <div className={sectionClass('max-w-7xl', 'relative py-7 sm:py-9 lg:py-10')}>
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-4xl">
              <p className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase text-emerald-100 backdrop-blur">
                <Activity className="h-4 w-4" aria-hidden="true" />
                Sports Connect
              </p>
              <h1 className="mt-4 max-w-4xl text-2xl font-semibold leading-8 sm:text-4xl sm:leading-tight">{title}</h1>
              <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-100 sm:text-base sm:leading-7">{description}</p>
            </div>
            <Link
              to="/sports/create-team"
              className="inline-flex min-h-[46px] w-full items-center justify-center gap-2 rounded-lg bg-white px-4 py-3 text-sm font-semibold text-slate-950 shadow-lg shadow-slate-950/15 transition hover:bg-emerald-50 sm:w-auto"
            >
              <Users className="h-4 w-4" aria-hidden="true" />
              Create Team
            </Link>
          </div>
          <nav className="-mx-4 mt-6 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0" aria-label="Sports module">
            {sportsAppLinks.map(({ to, label, icon: Icon }) => {
              const active = isActiveLink(to, label);
              return (
                <Link
                  key={to}
                  to={to}
                  className={`inline-flex min-h-[42px] flex-none items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition ${
                    active
                      ? 'border-white bg-white text-slate-950 shadow-lg shadow-slate-950/15'
                      : 'border-white/20 bg-white/10 text-white backdrop-blur hover:bg-white/15'
                  }`}
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                  {label}
                </Link>
              );
            })}
          </nav>
        </div>
      </section>
      {children}
    </div>
  );
};

const Message: React.FC<{ state: MessageState }> = ({ state }) => {
  if (!state) return null;
  return (
    <div className={`rounded-lg border px-4 py-3 text-sm leading-6 ${state.type === 'success' ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-rose-200 bg-rose-50 text-rose-800'}`}>
      {state.text}
    </div>
  );
};

const Field: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  type?: string;
  placeholder?: string;
}> = ({ label, value, onChange, required, type = 'text', placeholder }) => (
  <label className="block text-sm font-semibold text-slate-700">
    {label}
    {required && <span className="ml-1 text-rose-600">*</span>}
    <input
      type={type}
      value={value}
      required={required}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
      className="mt-2 min-h-[48px] w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
    />
  </label>
);

const PasswordField: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
}> = ({ label, value, onChange, required }) => {
  const inputId = useId();
  const [visible, setVisible] = useState(false);
  const actionLabel = `${visible ? 'Hide' : 'Show'} ${label.toLowerCase()}`;
  const Icon = visible ? EyeOff : Eye;

  return (
    <div className="block text-sm font-semibold text-slate-700">
      <label htmlFor={inputId}>
        {label}
        {required && <span className="ml-1 text-rose-600">*</span>}
      </label>
      <div className="relative mt-2">
        <input
          id={inputId}
          type={visible ? 'text' : 'password'}
          value={value}
          required={required}
          onChange={(event) => onChange(event.target.value)}
          className="min-h-[48px] w-full rounded-lg border border-slate-300 bg-white px-3 py-2 pr-12 text-sm text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
        />
        <button
          type="button"
          aria-label={actionLabel}
          title={actionLabel}
          onClick={() => setVisible((current) => !current)}
          className="absolute inset-y-0 right-2 my-auto flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-emerald-700 focus:outline-none focus:ring-4 focus:ring-emerald-100"
        >
          <Icon className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

const SelectField: React.FC<{
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  required?: boolean;
}> = ({ label, value, options, onChange, required }) => (
  <label className="block text-sm font-semibold text-slate-700">
    {label}
    {required && <span className="ml-1 text-rose-600">*</span>}
    <select
      value={value}
      required={required}
      onChange={(event) => onChange(event.target.value)}
      className="mt-2 min-h-[48px] w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
    >
      <option value="">Select</option>
      {options.map((option) => (
        <option key={option} value={option}>
          {formatEnum(option)}
        </option>
      ))}
    </select>
  </label>
);

const TextAreaField: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  placeholder?: string;
}> = ({ label, value, onChange, required, placeholder }) => (
  <label className="block text-sm font-semibold text-slate-700">
    {label}
    {required && <span className="ml-1 text-rose-600">*</span>}
    <textarea
      value={value}
      required={required}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
      rows={4}
      className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
    />
  </label>
);

const CheckboxField: React.FC<{
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}> = ({ label, checked, onChange }) => (
  <label className="flex min-h-[44px] items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-emerald-300 hover:bg-emerald-50/40">
    <input
      type="checkbox"
      checked={checked}
      onChange={(event) => onChange(event.target.checked)}
      className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
    />
    {label}
  </label>
);

type SportsLocationValue = RegisterPlayerPayload['location'];

const withSelectedOption = (options: string[], selected?: string) => {
  const value = String(selected || '').trim();
  return value && !options.includes(value) ? [value, ...options] : options;
};

const normalizeSportsLocation = (location?: Partial<SportsLocationValue>): SportsLocationValue => {
  const state = String(location?.state || '').trim();
  const district = String(location?.district || '').trim();
  const mandal = String(location?.mandal || '').trim();
  const village = String(location?.village || location?.area || '').trim();
  return {
    ...(location || {}),
    state,
    district,
    mandal,
    village,
    area: village || String(location?.area || '').trim() || mandal || district,
  };
};

const toSportsLocationQuery = (location: Partial<SportsLocationValue>) => ({
  state: location.state || '',
  district: location.district || '',
  mandal: location.mandal || '',
  village: location.village || '',
  area: location.village || location.area || '',
});

const compactRecord = (record: Record<string, any>) =>
  Object.fromEntries(Object.entries(record).filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== ''));

const LocationDropdownFields: React.FC<{
  value: SportsLocationValue;
  onChange: (location: SportsLocationValue) => void;
  required?: boolean;
  className?: string;
}> = ({ value, onChange, required, className = '' }) => {
  const location = normalizeSportsLocation(value);
  const stateValue = location.state || '';
  const districtValue = location.district || '';
  const mandalValue = location.mandal || '';
  const villageValue = location.village || '';
  const [stateOptions, setStateOptions] = useState<string[]>([]);
  const [districtOptions, setDistrictOptions] = useState<string[]>([]);
  const [mandalOptions, setMandalOptions] = useState<string[]>([]);
  const [villageOptions, setVillageOptions] = useState<string[]>([]);

  useEffect(() => {
    let active = true;
    locationApi.getStates().then((items) => {
      if (active) setStateOptions(items);
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    if (!stateValue) {
      setDistrictOptions([]);
      return undefined;
    }
    locationApi.getDistricts(stateValue).then((items) => {
      if (active) setDistrictOptions(items);
    });
    return () => {
      active = false;
    };
  }, [stateValue]);

  useEffect(() => {
    let active = true;
    if (!stateValue || !districtValue) {
      setMandalOptions([]);
      return undefined;
    }
    locationApi.getMandals(stateValue, districtValue).then((items) => {
      if (active) setMandalOptions(items);
    });
    return () => {
      active = false;
    };
  }, [districtValue, stateValue]);

  useEffect(() => {
    let active = true;
    if (!stateValue || !districtValue || !mandalValue) {
      setVillageOptions([]);
      return undefined;
    }
    locationApi.getVillages(stateValue, districtValue, mandalValue).then((items) => {
      if (active) setVillageOptions(items);
    });
    return () => {
      active = false;
    };
  }, [districtValue, mandalValue, stateValue]);

  const update = (patch: Partial<SportsLocationValue>) => onChange(normalizeSportsLocation({ ...location, ...patch }));

  return (
    <div className={`grid gap-4 md:grid-cols-4 ${className}`}>
      <SelectField
        label="State"
        value={stateValue}
        options={withSelectedOption(stateOptions, stateValue)}
        required={required}
        onChange={(state) => onChange(normalizeSportsLocation({ state, district: '', mandal: '', village: '', area: '' }))}
      />
      <SelectField
        label="District"
        value={districtValue}
        options={withSelectedOption(districtOptions, districtValue)}
        required={required}
        onChange={(district) => onChange(normalizeSportsLocation({ ...location, district, mandal: '', village: '', area: '' }))}
      />
      <SelectField
        label="Mandal"
        value={mandalValue}
        options={withSelectedOption(mandalOptions, mandalValue)}
        onChange={(mandal) => onChange(normalizeSportsLocation({ ...location, mandal, village: '', area: '' }))}
      />
      <SelectField
        label="Village"
        value={villageValue}
        options={withSelectedOption(villageOptions, villageValue)}
        required={required}
        onChange={(village) => update({ village, area: village })}
      />
    </div>
  );
};

type SportsAccountPayload = {
  firstName: string;
  lastName: string;
  email: string;
  username: string;
  phone: string;
  password: string;
  confirmPassword: string;
};

const createSportsAccountPayload = (): SportsAccountPayload => ({
  firstName: '',
  lastName: '',
  email: '',
  username: '',
  phone: '',
  password: '',
  confirmPassword: '',
});

const useSportsAccountFlow = () => {
  const { user, register, verifyRegistrationOtp, requestSportsAccountSwitchOtp, verifySportsAccountSwitchOtp } = useAuth();
  const signedInUser: any = (user as any)?.user ? (user as any).user : user;
  const isSportsAccount = String(signedInUser?.role || '').toUpperCase() === 'SPORTS';
  const currentUser = isSportsAccount ? signedInUser : null;
  const signedInNonSportsUser = signedInUser && !isSportsAccount ? signedInUser : null;
  const [accountPayload, setAccountPayload] = useState<SportsAccountPayload>(createSportsAccountPayload);
  const [registrationIdentifier, setRegistrationIdentifier] = useState('');
  const [otp, setOtp] = useState('');
  const [devOtp, setDevOtp] = useState<string | null>(null);
  const [awaitingOtp, setAwaitingOtp] = useState(false);
  const [otpMode, setOtpMode] = useState<'register' | 'sports_switch'>('register');
  const [switchPromptOpen, setSwitchPromptOpen] = useState(false);
  const [switchLoading, setSwitchLoading] = useState(false);
  const [switchError, setSwitchError] = useState('');

  const updateAccount = (patch: Partial<SportsAccountPayload>) =>
    setAccountPayload((current) => ({ ...current, ...patch }));

  const buildSportsSwitchPayload = (identifier: string, otpValue: string) => ({
    identifier,
    otp: otpValue,
    firstName: accountPayload.firstName.trim(),
    lastName: accountPayload.lastName.trim(),
    phone: normalizeSportsPhone(accountPayload.phone),
  });

  const validateSportsAccount = () => {
    if (!accountPayload.firstName.trim() || !accountPayload.lastName.trim() || !accountPayload.username.trim() || !accountPayload.phone.trim()) {
      throw new Error('First name, last name, username and phone are required to create your Sports Connect account.');
    }
    const normalizedPhone = normalizeSportsPhone(accountPayload.phone);
    if (!/^\+[1-9][0-9]{7,14}$/.test(normalizedPhone)) {
      throw new Error('Phone must include country code, for example +919876543210.');
    }
    if (accountPayload.password !== accountPayload.confirmPassword) {
      throw new Error('Sports Connect account passwords do not match.');
    }
    if (accountPayload.password.length < 8) {
      throw new Error('Password must be at least 8 characters.');
    }
    return normalizedPhone;
  };

  const confirmSportsAccountSwitch = async () => {
    setSwitchError('');
    setSwitchLoading(true);
    try {
      const normalizedPhone = validateSportsAccount();
      const response = await requestSportsAccountSwitchOtp(normalizedPhone);
      setRegistrationIdentifier(response?.identifier || normalizedPhone);
      setDevOtp(response?.otp || null);
      setOtp('');
      setOtpMode('sports_switch');
      setAwaitingOtp(true);
      setSwitchPromptOpen(false);
    } catch (error: any) {
      setSwitchError(resolveApiErrorMessage(error, 'Unable to send Sports Connect switch OTP.'));
    } finally {
      setSwitchLoading(false);
    }
  };

  const submitWithSportsAccount = async (
    submitSportsRecord: () => Promise<void>,
  ): Promise<'otp_sent' | 'switch_prompt' | 'submitted' | 'submitted_after_registration' | 'submitted_after_switch'> => {
    if (currentUser) {
      await submitSportsRecord();
      return 'submitted';
    }

    if (awaitingOtp) {
      if (!otp.trim()) throw new Error('Enter the OTP sent to your phone before submitting Sports Connect registration.');
      if (otpMode === 'sports_switch') {
        await verifySportsAccountSwitchOtp(buildSportsSwitchPayload(registrationIdentifier, otp.trim()));
        await submitSportsRecord();
        return 'submitted_after_switch';
      }
      await verifyRegistrationOtp(registrationIdentifier, otp.trim());
      await submitSportsRecord();
      return 'submitted_after_registration';
    }

    const normalizedPhone = validateSportsAccount();

    try {
      const response = await register({
        firstName: accountPayload.firstName.trim(),
        lastName: accountPayload.lastName.trim(),
        username: accountPayload.username.trim(),
        password: accountPayload.password,
        role: 'SPORTS',
        phone: normalizedPhone,
        ...(accountPayload.email.trim() ? { email: accountPayload.email.trim() } : {}),
      });
      setRegistrationIdentifier(response?.identifier || normalizedPhone);
      setDevOtp(response?.otp || null);
      setOtp('');
      setOtpMode('register');
      setAwaitingOtp(true);
      return 'otp_sent';
    } catch (error) {
      if (isAccountConflictError(error)) {
        setSwitchPromptOpen(true);
        setSwitchError('');
        return 'switch_prompt';
      }
      throw error;
    }
  };

  return {
    currentUser,
    signedInNonSportsUser,
    needsAccountRegistration: !currentUser,
    accountPayload,
    updateAccount,
    awaitingOtp,
    otp,
    setOtp,
    devOtp,
    otpMode,
    switchPromptOpen,
    switchLoading,
    switchError,
    setSwitchPromptOpen,
    confirmSportsAccountSwitch,
    submitWithSportsAccount,
  };
};

const SportsAccountPanel: React.FC<{
  flow: ReturnType<typeof useSportsAccountFlow>;
  title: string;
}> = ({ flow, title }) => {
  const nextPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (!flow.needsAccountRegistration) {
    const sportsRoles = Array.isArray(flow.currentUser?.sportsRoles) ? flow.currentUser.sportsRoles : [];
    return (
      <div className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm leading-6 text-emerald-950">
        <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-emerald-700" aria-hidden="true" />
        <div>
          <p className="font-semibold">Signed in as {flow.currentUser?.firstName || flow.currentUser?.username || 'LocalAlly user'}</p>
          <p className="mt-1 text-emerald-900">
            This Sports Connect record will be linked to your account.
            {sportsRoles.length ? ` Sports roles: ${sportsRoles.map(formatEnum).join(', ')}.` : ''}
          </p>
        </div>
      </div>
    );
  }

  if (flow.awaitingOtp) {
    return (
      <div className="space-y-4 rounded-lg border border-amber-200 bg-amber-50 p-4 shadow-[0_16px_40px_-34px_rgba(217,119,6,0.5)] sm:p-5">
        <div className="flex items-start gap-3">
          <ShieldCheck className="mt-0.5 h-5 w-5 flex-none text-amber-700" aria-hidden="true" />
          <div>
          <h2 className="text-base font-semibold text-amber-950">Verify Sports Connect account</h2>
          <p className="mt-1 text-sm text-amber-900">
            {flow.otpMode === 'sports_switch'
              ? 'Enter the OTP to switch your existing account to Sports Connect, then submit this sports registration.'
              : 'Enter the OTP to activate your Sports Connect account, then submit this sports registration.'}
            {flow.devOtp ? ` Dev OTP: ${flow.devOtp}` : ''}
          </p>
          </div>
        </div>
        <Field label="OTP" value={flow.otp} required onChange={flow.setOtp} />
      </div>
    );
  }

  return (
    <div className="space-y-5 rounded-lg border border-emerald-200 bg-[linear-gradient(180deg,_#ecfdf5_0%,_#ffffff_100%)] p-4 sm:p-5">
      {flow.switchPromptOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/60 px-4">
          <div className="w-full max-w-md rounded-lg bg-white p-5 text-slate-900 shadow-2xl">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-amber-50 text-amber-700">
                <ShieldCheck className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <h2 className="text-lg font-semibold">Account already exists</h2>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  A LocalAlly account already exists with this phone. Do you want to switch that account to Sports Connect and continue this registration?
                </p>
              </div>
            </div>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              This changes the account workspace after OTP verification. Existing login details stay the same.
            </p>
            {flow.switchError && (
              <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
                {flow.switchError}
              </div>
            )}
            <div className="mt-5 grid grid-cols-2 gap-3">
              <SecondaryButton type="button" disabled={flow.switchLoading} onClick={() => flow.setSwitchPromptOpen(false)}>
                No
              </SecondaryButton>
              <PrimaryButton type="button" disabled={flow.switchLoading} onClick={() => void flow.confirmSportsAccountSwitch()}>
                {flow.switchLoading ? 'Sending OTP...' : 'Yes'}
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-emerald-700 text-white">
          <UserPlus className="h-5 w-5" aria-hidden="true" />
        </span>
        <div>
          <h2 className="text-base font-semibold text-emerald-950">{title}</h2>
          <p className="mt-1 text-sm leading-6 text-emerald-900">
            Create a Sports Connect account for sports only. It is separate from student and teacher registration; sports roles are linked after OTP verification.
          </p>
        </div>
      </div>
      {flow.signedInNonSportsUser && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm leading-6 text-amber-900">
          You are signed in as {formatEnum(flow.signedInNonSportsUser.role)}. Sports Connect needs a separate sports account, so OTP verification here will switch you to that sports workspace.
        </div>
      )}
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="First Name" value={flow.accountPayload.firstName} required onChange={(value) => flow.updateAccount({ firstName: value })} />
        <Field label="Last Name" value={flow.accountPayload.lastName} required onChange={(value) => flow.updateAccount({ lastName: value })} />
        <Field label="Username" value={flow.accountPayload.username} required onChange={(value) => flow.updateAccount({ username: value })} />
        <Field label="Phone" value={flow.accountPayload.phone} required placeholder="+919876543210" onChange={(value) => flow.updateAccount({ phone: value })} />
        <Field label="Email" type="email" value={flow.accountPayload.email} onChange={(value) => flow.updateAccount({ email: value })} />
        <PasswordField label="Password" value={flow.accountPayload.password} required onChange={(value) => flow.updateAccount({ password: value })} />
        <PasswordField label="Confirm Password" value={flow.accountPayload.confirmPassword} required onChange={(value) => flow.updateAccount({ confirmPassword: value })} />
      </div>
      <p className="text-sm text-emerald-900">
        Already have an account? <Link className="font-semibold text-emerald-800 underline" to={`/login?next=${encodeURIComponent(nextPath)}`}>Sign in and continue Sports Connect registration</Link>.
      </p>
    </div>
  );
};

const PrimaryButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({ children, className = '', ...props }) => (
  <button
    {...props}
    className={`inline-flex min-h-[48px] items-center justify-center gap-2 rounded-lg bg-emerald-700 px-5 py-3 text-sm font-semibold text-white shadow-[0_14px_32px_-24px_rgba(4,120,87,0.9)] transition hover:bg-emerald-800 focus:outline-none focus:ring-4 focus:ring-emerald-200 disabled:cursor-not-allowed disabled:opacity-60 ${className}`}
  >
    {children}
  </button>
);

const SecondaryButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({ children, className = '', ...props }) => (
  <button
    {...props}
    className={`inline-flex min-h-[48px] items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-800 transition hover:border-emerald-300 hover:bg-emerald-50 focus:outline-none focus:ring-4 focus:ring-emerald-100 disabled:cursor-not-allowed disabled:opacity-60 ${className}`}
  >
    {children}
  </button>
);

const StatusPill: React.FC<{ value?: string }> = ({ value }) => {
  const normalized = String(value || '').toUpperCase();
  const color =
    normalized.includes('APPROVED') || normalized.includes('VERIFIED') || normalized.includes('OPEN')
      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
      : normalized.includes('REJECTED') || normalized.includes('DISPUTED') || normalized.includes('CANCELLED')
        ? 'border-rose-200 bg-rose-50 text-rose-700'
        : 'border-amber-200 bg-amber-50 text-amber-700';
  return <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${color}`}>{formatEnum(value)}</span>;
};

const LinkCard: React.FC<{
  to: string;
  title: string;
  description: string;
  icon: React.ElementType;
}> = ({ to, title, description, icon: Icon }) => (
  <Link to={to} className="group flex min-h-[184px] flex-col rounded-lg border border-slate-200 bg-white p-4 shadow-[0_18px_48px_-38px_rgba(15,23,42,0.3)] transition hover:-translate-y-0.5 hover:border-emerald-300 hover:shadow-[0_24px_58px_-40px_rgba(4,120,87,0.5)] sm:p-5">
    <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100">
      <Icon className="h-5 w-5" aria-hidden="true" />
    </span>
    <h2 className="mt-4 text-lg font-semibold leading-6 text-slate-950">{title}</h2>
    <p className="mt-2 text-sm leading-6 text-slate-600">{description}</p>
    <span className="mt-auto inline-flex items-center gap-2 pt-4 text-sm font-semibold text-emerald-700">
      Open <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" aria-hidden="true" />
    </span>
  </Link>
);

const Stat: React.FC<{ title: string; value: string | number }> = ({ title, value }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-[0_16px_42px_-36px_rgba(15,23,42,0.3)] sm:p-5">
    <p className="text-xs font-semibold uppercase text-slate-500">{title}</p>
    <p className="mt-2 break-words text-2xl font-semibold leading-8 text-slate-950">{value}</p>
  </div>
);

const TeamCard: React.FC<{ team: any }> = ({ team }) => (
  <Link to={`/sports/teams/${getRecordId(team)}`} className="group rounded-lg border border-slate-200 bg-white p-4 shadow-[0_18px_46px_-38px_rgba(15,23,42,0.28)] transition hover:-translate-y-0.5 hover:border-emerald-300 hover:shadow-[0_24px_58px_-42px_rgba(4,120,87,0.5)] sm:p-5">
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase text-emerald-700">
          <Users className="h-4 w-4" aria-hidden="true" />
          {formatEnum(team.sport)}
        </div>
        <h3 className="mt-2 text-lg font-semibold leading-6 text-slate-950">{team.teamName}</h3>
        <p className="mt-2 flex items-start gap-2 text-sm leading-6 text-slate-600">
          <MapPin className="mt-0.5 h-4 w-4 flex-none text-slate-400" aria-hidden="true" />
          <span>{[team.location?.area, team.location?.mandal].filter(Boolean).join(', ') || 'Location pending'}</span>
        </p>
      </div>
      <StatusPill value={team.status} />
    </div>
    <div className="mt-4 flex flex-wrap gap-2 text-xs font-semibold">
      <span className="rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-slate-700">{formatEnum(team.teamLevel)}</span>
      {team.homeGround && <span className="rounded-full border border-amber-100 bg-amber-50 px-3 py-1 text-amber-700">{team.homeGround}</span>}
      <span className="rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1 text-emerald-700">{team.stats?.wins || 0} wins</span>
    </div>
    <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-emerald-700">
      View team <ChevronRight className="h-4 w-4 transition group-hover:translate-x-0.5" aria-hidden="true" />
    </span>
  </Link>
);

const TournamentCard: React.FC<{ tournament: any }> = ({ tournament }) => (
  <Link to={`/sports/tournaments/${getRecordId(tournament)}`} className="group rounded-lg border border-slate-200 bg-white p-4 shadow-[0_18px_46px_-38px_rgba(15,23,42,0.28)] transition hover:-translate-y-0.5 hover:border-emerald-300 hover:shadow-[0_24px_58px_-42px_rgba(4,120,87,0.5)] sm:p-5">
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase text-orange-700">
          <Trophy className="h-4 w-4" aria-hidden="true" />
          {formatEnum(tournament.sport)}
        </div>
        <h3 className="mt-2 text-lg font-semibold leading-6 text-slate-950">{tournament.name}</h3>
        <p className="mt-2 flex items-start gap-2 text-sm leading-6 text-slate-600">
          <MapPin className="mt-0.5 h-4 w-4 flex-none text-slate-400" aria-hidden="true" />
          <span>{[getSportsLocationLabel(tournament.location), tournament.groundName].filter(Boolean).join(', ') || 'Location pending'}</span>
        </p>
      </div>
      <StatusPill value={tournament.status} />
    </div>
    <div className="mt-4 flex flex-wrap gap-2 text-xs font-semibold">
      <span className="rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-slate-700">{formatEnum(tournament.format)}</span>
      <span className="rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1 text-emerald-700">Max {tournament.maxTeams} teams</span>
      {typeof tournament.entryFee === 'number' && <span className="rounded-full border border-amber-100 bg-amber-50 px-3 py-1 text-amber-700">INR {tournament.entryFee}</span>}
    </div>
    <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-emerald-700">
      View tournament <ChevronRight className="h-4 w-4 transition group-hover:translate-x-0.5" aria-hidden="true" />
    </span>
  </Link>
);

const createLocation = (): RegisterPlayerPayload['location'] => ({ area: '', village: '', mandal: '', district: '', state: 'Andhra Pradesh' });
const createSearchLocation = (): RegisterPlayerPayload['location'] => ({ area: '', village: '', mandal: '', district: '', state: '' });

const isSportsLocationReady = (location?: Partial<SportsLocationValue>) =>
  Boolean(location?.state && location?.district && (location?.village || location?.area));

const getSportsLocationLabel = (location?: Partial<SportsLocationValue>) =>
  [location?.village || location?.area, location?.mandal, location?.district].filter(Boolean).join(', ');

const makePlaceSchema = (location?: Partial<SportsLocationValue>, name?: string) => {
  const label = name || getSportsLocationLabel(location);
  if (!label && !location?.state) return undefined;
  return stripEmptySchemaValues({
    '@type': 'Place',
    name: label || location?.state,
    address: stripEmptySchemaValues({
      '@type': 'PostalAddress',
      addressLocality: location?.village || location?.area || location?.mandal || location?.district,
      addressRegion: location?.state,
      addressCountry: 'IN',
    }),
  });
};

const makeCollectionPageSchemas = ({
  title,
  description,
  path,
  items,
}: {
  title: string;
  description: string;
  path: string;
  items: Array<{ name?: string; path: string }>;
}): object[] => [
  ...makeSportsPageSchemas(title, description, path),
  {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: title,
    description,
    url: `${SITE_URL}${path}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: items
        .filter((item) => item.name && item.path)
        .map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.name,
          url: `${SITE_URL}${item.path}`,
        })),
    },
  },
];

const makeTeamDetailSchemas = (team: any, path: string, description: string): object[] => [
  ...makeSportsPageSchemas(team?.teamName || 'Team Profile', description, path, 'Local sports team profile and roster discovery'),
  stripEmptySchemaValues({
    '@context': 'https://schema.org',
    '@type': 'SportsTeam',
    name: team?.teamName,
    sport: formatEnum(team?.sport),
    url: `${SITE_URL}${path}`,
    location: makePlaceSchema(team?.location, team?.homeGround),
    description: team?.description || description,
  }),
];

const makeTournamentDetailSchemas = (tournament: any, path: string, description: string): object[] => [
  ...makeSportsPageSchemas(tournament?.name || 'Tournament', description, path, 'Local sports tournament registration and discovery'),
  stripEmptySchemaValues({
    '@context': 'https://schema.org',
    '@type': 'SportsEvent',
    name: tournament?.name,
    sport: formatEnum(tournament?.sport),
    url: `${SITE_URL}${path}`,
    startDate: tournament?.startDate,
    endDate: tournament?.endDate,
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    location: makePlaceSchema(tournament?.location, tournament?.groundName),
    organizer: {
      '@type': 'Organization',
      name: tournament?.organizerName || 'LocalAlly Sports Connect organizer',
    },
    description: tournament?.rules || description,
    offers:
      typeof tournament?.entryFee === 'number'
        ? {
            '@type': 'Offer',
            price: tournament.entryFee,
            priceCurrency: 'INR',
            availability: 'https://schema.org/InStock',
          }
        : undefined,
  }),
];

const getPlayerRoleForSport = (player: ExistingSportsPlayer, sport: SportCategoryName) =>
  player.sportProfiles?.find((profile) => profile.sport === sport)?.role || '';

export const SportsHomePage: React.FC = () => {
  const [overview, setOverview] = useState<any>(null);

  useEffect(() => {
    sportsService.overview().then(setOverview).catch(() => setOverview(null));
  }, []);

  const featuredTeams = overview?.featuredTeams || [];
  const upcomingTournaments = overview?.upcomingTournaments || [];
  const grounds = overview?.grounds || [];

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,_#f8fafc_0%,_#eefdf5_42%,_#f8fafc_100%)] text-slate-950">
      <SEO
        title="LocalAlly Sports Connect | Local Teams and Tournaments"
        description="Connect local players, captains, teams, tournaments, grounds and sponsors for village and town sports communities."
        path="/sports"
        image={SPORTS_HERO_IMAGE}
        schema={[
          ...(Array.isArray(pageSchema) ? pageSchema : [pageSchema]),
          {
            '@context': 'https://schema.org',
            '@type': 'WebApplication',
            name: 'LocalAlly Sports Connect',
            applicationCategory: 'SportsApplication',
            operatingSystem: 'Web',
            url: `${SITE_URL}/sports`,
            image: SPORTS_HERO_IMAGE,
            description:
              'LocalAlly Sports Connect helps local players, captains, teams, tournament organizers, ground owners and sponsors connect across nearby sports communities.',
          },
          {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: 'LocalAlly Sports Connect discovery highlights',
            itemListElement: [
              ...featuredTeams.slice(0, 3).map((team: any, index: number) => ({
                '@type': 'ListItem',
                position: index + 1,
                name: team.teamName,
                url: `${SITE_URL}/sports/teams/${getRecordId(team)}`,
              })),
              ...upcomingTournaments.slice(0, 3).map((tournament: any, index: number) => ({
                '@type': 'ListItem',
                position: featuredTeams.slice(0, 3).length + index + 1,
                name: tournament.name,
                url: `${SITE_URL}/sports/tournaments/${getRecordId(tournament)}`,
              })),
            ],
          },
        ]}
      />
      <section
        className="relative border-b border-slate-200 bg-slate-950 bg-cover bg-center text-white"
        style={{ backgroundImage: `linear-gradient(90deg, rgba(2,6,23,0.9), rgba(6,78,59,0.72), rgba(234,88,12,0.30)), url(${SPORTS_HERO_IMAGE})` }}
      >
        <div className="mx-auto grid min-h-[430px] max-w-7xl items-center gap-8 px-4 py-10 sm:px-6 sm:py-12 lg:grid-cols-[minmax(0,1fr)_380px] lg:px-8">
          <div>
            <p className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase text-emerald-100 backdrop-blur">
              <Activity className="h-4 w-4" aria-hidden="true" />
              Village and town sports
            </p>
            <h1 className="mt-4 max-w-4xl text-4xl font-semibold leading-tight sm:text-6xl">LocalAlly Sports Connect</h1>
            <p className="mt-5 max-w-2xl text-base leading-7 text-slate-100">
              Build local teams, run tournaments, find grounds, and keep player history connected after mobile verification.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/sports/create-team" className="inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg bg-white px-5 py-3 text-sm font-semibold text-slate-950 shadow-lg shadow-slate-950/20 transition hover:bg-emerald-50 sm:w-auto">
                <Users className="h-4 w-4" /> Create Team
              </Link>
              <Link to="/sports/tournaments" className="inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg border border-white/70 bg-white/10 px-5 py-3 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/15 sm:w-auto">
                <Trophy className="h-4 w-4" /> Join Tournament
              </Link>
            </div>
          </div>
          <div className="rounded-lg border border-white/20 bg-white/10 p-4 backdrop-blur sm:p-5">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <p className="text-2xl font-semibold leading-8">{featuredTeams.length}</p>
                <p className="mt-1 text-xs font-semibold uppercase text-emerald-100">Teams</p>
              </div>
              <div>
                <p className="text-2xl font-semibold leading-8">{upcomingTournaments.length}</p>
                <p className="mt-1 text-xs font-semibold uppercase text-emerald-100">Events</p>
              </div>
              <div>
                <p className="text-2xl font-semibold leading-8">{grounds.length}</p>
                <p className="mt-1 text-xs font-semibold uppercase text-emerald-100">Grounds</p>
              </div>
            </div>
            <div className="mt-5 space-y-3 border-t border-white/20 pt-5 text-sm leading-6 text-slate-100">
              <p className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-200" aria-hidden="true" /> Captain-led team creation</p>
              <p className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-200" aria-hidden="true" /> Player claim and verification flow</p>
              <p className="flex items-center gap-2"><Flag className="h-4 w-4 text-orange-200" aria-hidden="true" /> Tournaments, grounds, and sponsors</p>
            </div>
          </div>
        </div>
      </section>

      <section className={sectionClass('max-w-7xl', '-mt-4 relative')}>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <LinkCard to="/sports/register" title="Sports Registration" description="Choose player, team captain, organizer, ground owner or sponsor onboarding." icon={UserPlus} />
          <LinkCard to="/sports/create-team" title="Create Team" description="Captain registration first. Add registered, invited, or unclaimed players manually." icon={Users} />
          <LinkCard to="/sports/teams" title="Find Nearby Teams" description="Search local teams by sport, village, mandal, level and availability." icon={Search} />
          <LinkCard to="/sports/challenge-match" title="Challenge Match" description="Send friendly or practice match challenges with venue, rules and timing." icon={Target} />
          <LinkCard to="/sports/tournaments" title="Join Tournament" description="Register matching-sport teams and review rules before submitting." icon={Trophy} />
          <LinkCard to="/sports/tournaments/create" title="Create Tournament" description="Organizers can create tournaments with verification rules and fixture generation." icon={Flag} />
          <LinkCard to="/sports/profile" title="My Sports Profile" description="Track sport profiles, teams, claims and performance history." icon={Activity} />
          <LinkCard to="/sports/grounds" title="Grounds" description="Discover or list local grounds for matches, practice and tournaments." icon={Building2} />
          <LinkCard to="/sports/claim-profile" title="Claim Profile" description="Claim a player record added by your team captain." icon={ShieldCheck} />
        </div>
      </section>

      <section className="border-y border-slate-200 bg-white/80">
        <div className={sectionClass('max-w-7xl', 'grid gap-6 lg:grid-cols-2')}>
          <div>
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-2xl font-semibold text-slate-950">Featured Teams</h2>
              <Link to="/sports/teams" className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-700">View all <ChevronRight className="h-4 w-4" /></Link>
            </div>
            <div className="mt-4 grid gap-4">
              {featuredTeams.slice(0, 3).map((team: any) => <TeamCard key={getRecordId(team)} team={team} />)}
              {!featuredTeams.length && <div className="rounded-lg border border-dashed border-slate-300 bg-white p-5 text-sm text-slate-600">No approved teams yet.</div>}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-2xl font-semibold text-slate-950">Upcoming Tournaments</h2>
              <Link to="/sports/tournaments" className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-700">View all <ChevronRight className="h-4 w-4" /></Link>
            </div>
            <div className="mt-4 grid gap-4">
              {upcomingTournaments.slice(0, 3).map((tournament: any) => <TournamentCard key={getRecordId(tournament)} tournament={tournament} />)}
              {!upcomingTournaments.length && <div className="rounded-lg border border-dashed border-slate-300 bg-white p-5 text-sm text-slate-600">No open tournaments yet.</div>}
            </div>
          </div>
        </div>
      </section>

      <section className={sectionClass('max-w-7xl', 'py-5 sm:py-8')}>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-5 text-sm leading-6 text-amber-950">
          LocalAlly Sports Connect helps players, teams, and organizers connect locally. All teams and organizers are responsible for following local rules, ground permissions, safety precautions, and fair play. Betting or gambling is strictly not allowed on LocalAlly.
        </div>
      </section>
    </div>
  );
};

export const SportsRegistrationPage: React.FC = () => (
  <PageShell
    title="Sports Connect Registration"
    description="Register separately for the Sports Connect module as a player, captain, organizer, ground owner or sponsor."
    path="/sports/register"
    schema={makeSportsPageSchemas(
      'Sports Connect Registration',
      'Register separately for the Sports Connect module as a player, captain, organizer, ground owner or sponsor.',
      '/sports/register',
      'Sports player, captain, organizer, ground owner and sponsor registration',
    )}
  >
    <section className={sectionClass()}>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <LinkCard to="/sports/register-player" title="Register as Player" description="Create your personal sports profile and sport-wise roles." icon={UserPlus} />
        <LinkCard to="/sports/create-team" title="Register as Captain" description="Create a team with captain verification and manual player addition." icon={Users} />
        <LinkCard to="/sports/tournaments/create" title="Register as Organizer" description="Create local tournaments, set verification rules and manage fixtures." icon={Trophy} />
        <LinkCard to="/sports/grounds" title="Register Ground" description="List a local ground for practice, friendly matches and tournaments." icon={Building2} />
        <LinkCard to="/sports/sponsors" title="Sponsor Sports" description="Support tournaments through banners, prizes and local promotion." icon={Handshake} />
        <LinkCard to="/sports/claim-profile" title="Claim Player Profile" description="Link a captain-added player record to your verified account." icon={ShieldCheck} />
      </div>
    </section>
  </PageShell>
);

export const SportsDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const currentUser: any = (user as any)?.user ? (user as any).user : user;
  const [data, setData] = useState<any>(null);
  const [matches, setMatches] = useState<any[]>([]);
  const [tournaments, setTournaments] = useState<any[]>([]);

  useEffect(() => {
    if (!currentUser) return;
    sportsService.myPlayerProfile().then(setData).catch(() => setData(null));
    sportsService.matches().then((items) => setMatches(Array.isArray(items) ? items : [])).catch(() => setMatches([]));
    sportsService.tournaments().then((items) => setTournaments(Array.isArray(items) ? items : [])).catch(() => setTournaments([]));
  }, [currentUser]);

  if (!currentUser) {
    return <SportsRegistrationPage />;
  }

  const myTeamIds = new Set((data?.teams || []).map((team: any) => getRecordId(team)));
  const myMatches = matches.filter((match) => myTeamIds.has(match.teamAId) || myTeamIds.has(match.teamBId));

  return (
    <PageShell title="Sports Dashboard" description="Manage Sports Connect registrations, teams, tournaments, claims and match activity." path="/sports/dashboard" robots="noindex, nofollow">
      <section className={sectionClass()}>
        <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4">
          <Stat title="Sports profiles" value={data?.sportProfiles?.length || 0} />
          <Stat title="My teams" value={data?.teams?.length || 0} />
          <Stat title="Roster links" value={data?.teamPlayers?.length || 0} />
          <Stat title="My matches" value={myMatches.length} />
        </div>

        <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <LinkCard to="/rides/book" title="Book Ride" description="Request a local auto, bike, cab or shared ride from this account." icon={MapPin} />
          <LinkCard to="/sports/register" title="Registration" description="Add a sports role or complete onboarding." icon={UserPlus} />
          <LinkCard to="/sports/create-team" title="Create Team" description="Register a captain-led team." icon={Users} />
          <LinkCard to="/sports/challenge-match" title="Challenge Match" description="Send a local match request." icon={Target} />
          <LinkCard to="/sports/claim-profile" title="Claim Profile" description="Link unclaimed roster history." icon={ShieldCheck} />
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          <section>
            <h2 className="text-2xl font-semibold text-slate-950">My Teams</h2>
            <div className="mt-4 grid gap-4">
              {(data?.teams || []).map((team: any) => <TeamCard key={getRecordId(team)} team={team} />)}
              {!data?.teams?.length && <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600 shadow-sm sm:p-5">No sports teams linked yet.</div>}
            </div>
          </section>
          <section>
            <h2 className="text-2xl font-semibold text-slate-950">Open Tournaments</h2>
            <div className="mt-4 grid gap-4">
              {tournaments.slice(0, 4).map((tournament) => <TournamentCard key={getRecordId(tournament)} tournament={tournament} />)}
              {!tournaments.length && <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600 shadow-sm sm:p-5">No open tournaments yet.</div>}
            </div>
          </section>
        </div>
      </section>
    </PageShell>
  );
};

export const RegisterPlayerPage: React.FC = () => {
  const accountFlow = useSportsAccountFlow();
  const [payload, setPayload] = useState<RegisterPlayerPayload>({
    name: '',
    phone: '',
    location: createLocation(),
    sports: [{ sport: 'CRICKET', role: '', experienceLevel: 'LOCAL' }],
  });
  const [message, setMessage] = useState<MessageState>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!accountFlow.currentUser) return;
    setPayload((current) => ({
      ...current,
      name: current.name || [accountFlow.currentUser.firstName, accountFlow.currentUser.lastName].filter(Boolean).join(' ').trim(),
      phone: current.phone || accountFlow.currentUser.phone || '',
    }));
  }, [accountFlow.currentUser]);

  const updateSport = (index: number, patch: Partial<RegisterPlayerPayload['sports'][number]>) => {
    setPayload((current) => ({
      ...current,
      sports: current.sports.map((sport, sportIndex) => {
        if (sportIndex !== index) return sport;
        const nextSport = { ...sport, ...patch };
        if (patch.sport && !getPlayingRoleOptions(patch.sport).includes(nextSport.role)) {
          nextSport.role = '';
        }
        return nextSport;
      }),
    }));
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setMessage(null);
    try {
      const result = await accountFlow.submitWithSportsAccount(async () => {
        await sportsService.registerPlayer(payload);
      });
      if (result === 'switch_prompt') return;
      setMessage({
        type: 'success',
        text:
          result === 'otp_sent'
            ? 'Account OTP sent. Enter OTP and submit again to finish Sports Connect player registration.'
            : 'Sports player profile saved. Your Sports dashboard is now available.',
      });
    } catch (error: any) {
      setMessage({ type: 'error', text: resolveApiErrorMessage(error, 'Unable to save player profile.') });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell
      title="Create Player Profile"
      description="Register as an individual player and maintain sport-wise roles and performance."
      path="/sports/register-player"
      schema={makeSportsPageSchemas(
        'Create Player Profile',
        'Register as an individual player and maintain sport-wise roles and performance.',
        '/sports/register-player',
        'Local sports player profile registration',
      )}
    >
      <section className={sectionClass('max-w-5xl')}>
        <form onSubmit={submit} className={panelClass('space-y-5')}>
          <Message state={message} />
          <SportsAccountPanel flow={accountFlow} title="Create Sports Player Account" />
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Name" value={payload.name} required onChange={(value) => setPayload((current) => ({ ...current, name: value }))} />
            <Field label="Phone number" value={payload.phone} required onChange={(value) => setPayload((current) => ({ ...current, phone: value }))} />
            <Field label="Age" type="number" value={payload.age ? String(payload.age) : ''} onChange={(value) => setPayload((current) => ({ ...current, age: parseNumber(value) }))} />
            <SelectField label="Gender" value={payload.gender || ''} options={genderOptions} onChange={(value) => setPayload((current) => ({ ...current, gender: value as GenderCategory }))} />
            <Field label="Availability" value={payload.availability || ''} placeholder="Weekends, evenings, mornings" onChange={(value) => setPayload((current) => ({ ...current, availability: value }))} />
            <LocationDropdownFields
              value={payload.location}
              required
              className="md:col-span-2"
              onChange={(location) => setPayload((current) => ({ ...current, location }))}
            />
          </div>

          <div className="space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-lg font-semibold text-slate-950">Sports</h2>
              <SecondaryButton type="button" className="w-full sm:w-auto" onClick={() => setPayload((current) => ({ ...current, sports: [...current.sports, { sport: 'CRICKET', role: '', experienceLevel: 'LOCAL' }] }))}>
                <Plus className="h-4 w-4" /> Add Sport
              </SecondaryButton>
            </div>
            {payload.sports.map((sport, index) => (
              <div key={index} className="grid gap-4 rounded-lg border border-slate-200 bg-slate-50 p-4 sm:p-5 md:grid-cols-3">
                <SelectField label="Sport" value={sport.sport} options={sportOptions} required onChange={(value) => updateSport(index, { sport: value as SportCategoryName })} />
                <SelectField label="Playing role" value={sport.role} options={getPlayingRoleOptions(sport.sport)} required onChange={(value) => updateSport(index, { role: value })} />
                <SelectField label="Experience level" value={sport.experienceLevel || 'LOCAL'} options={levelOptions} onChange={(value) => updateSport(index, { experienceLevel: value as ExperienceLevel })} />
              </div>
            ))}
          </div>
          <PrimaryButton type="submit" disabled={submitting} className="w-full sm:w-auto">
            <UserPlus className="h-4 w-4" /> {submitting ? 'Saving...' : 'Save Player Profile'}
          </PrimaryButton>
        </form>
      </section>
    </PageShell>
  );
};

const emptyManualPlayer = (): ManualTeamPlayerPayload => ({
  playerName: '',
  phone: '',
  role: '',
  age: undefined,
  jerseyNumber: '',
  village: '',
  sendInvite: false,
});

export const CreateTeamPage: React.FC = () => {
  const accountFlow = useSportsAccountFlow();
  const [step, setStep] = useState(1);
  const [payload, setPayload] = useState<CreateTeamPayload>({
    captain: { name: '', mobileNumber: '', location: createLocation(), addAsPlayer: true },
    team: { teamName: '', sport: 'CRICKET', location: createLocation(), teamLevel: 'LOCAL', preferredMatchDays: [], preferredMatchTimings: [] },
    players: [emptyManualPlayer(), emptyManualPlayer()],
  });
  const [message, setMessage] = useState<MessageState>(null);
  const [submitting, setSubmitting] = useState(false);
  const [teamSubmitted, setTeamSubmitted] = useState(false);
  const [playerSearchMode, setPlayerSearchMode] = useState<'existing' | 'manual'>('existing');
  const [playerSearch, setPlayerSearch] = useState({ q: '', phone: '' });
  const [playerSearchResults, setPlayerSearchResults] = useState<ExistingSportsPlayer[]>([]);
  const [playerSearchLoading, setPlayerSearchLoading] = useState(false);
  const [playerSearchMessage, setPlayerSearchMessage] = useState('');

  useEffect(() => {
    if (!accountFlow.currentUser) return;
    setPayload((current) => ({
      ...current,
      captain: {
        ...current.captain,
        name:
          current.captain.name ||
          [accountFlow.currentUser.firstName, accountFlow.currentUser.lastName].filter(Boolean).join(' ').trim(),
        mobileNumber: current.captain.mobileNumber || accountFlow.currentUser.phone || '',
      },
    }));
  }, [accountFlow.currentUser]);

  useEffect(() => {
    if (payload.captain.mobileNumber || !accountFlow.accountPayload.phone) return;
    setPayload((current) => ({
      ...current,
      captain: { ...current.captain, mobileNumber: normalizeSportsPhone(accountFlow.accountPayload.phone) },
    }));
  }, [accountFlow.accountPayload.phone, payload.captain.mobileNumber]);

  const captainContactPhone =
    normalizeSportsPhone(accountFlow.currentUser?.phone || accountFlow.accountPayload.phone || payload.captain.mobileNumber || '');
  const captainPhoneReady = /^\+[1-9][0-9]{7,14}$/.test(captainContactPhone);
  const captainComplete = Boolean(payload.captain.name.trim() && captainPhoneReady && isSportsLocationReady(payload.captain.location));
  const teamComplete = Boolean(payload.team.teamName.trim() && payload.team.sport && isSportsLocationReady(payload.team.location));

  const getStepLockMessage = (targetStep: number) => {
    if (targetStep <= 1) return '';
    if (!captainComplete) return 'Complete captain name, Sports account phone with country code, and captain location before moving ahead.';
    if (targetStep <= 2) return '';
    if (!teamComplete) return 'Complete team name, sport, and team location before adding players.';
    return '';
  };

  const goToStep = (targetStep: number) => {
    const lockMessage = getStepLockMessage(targetStep);
    if (lockMessage) {
      setMessage({ type: 'error', text: lockMessage });
      return;
    }
    setMessage(null);
    setStep(targetStep);
  };

  const updateCaptain = (patch: Partial<CreateTeamPayload['captain']>) => setPayload((current) => ({ ...current, captain: { ...current.captain, ...patch } }));
  const updateTeam = (patch: Partial<CreateTeamPayload['team']>) =>
    setPayload((current) => {
      const nextSport = patch.sport || current.team.sport;
      return {
        ...current,
        team: { ...current.team, ...patch },
        players: patch.sport
          ? (current.players || []).map((player) =>
              player.role && !getPlayingRoleOptions(nextSport).includes(player.role) ? { ...player, role: '' } : player,
            )
          : current.players,
      };
    });
  const updatePlayer = (index: number, patch: Partial<ManualTeamPlayerPayload>) => {
    setPayload((current) => ({
      ...current,
      players: (current.players || []).map((player, playerIndex) => (playerIndex === index ? { ...player, ...patch } : player)),
    }));
  };

  const playerFromExistingProfile = (player: ExistingSportsPlayer): ManualTeamPlayerPayload => ({
    playerName: player.name,
    phone: player.phone || '',
    role: getPlayerRoleForSport(player, payload.team.sport) || '',
    age: player.age,
    photo: player.profilePhoto || '',
    village: player.location?.village || player.location?.area || '',
    registeredPlayerProfileId: player.id,
    registeredUserId: player.userId,
    sendInvite: false,
  });

  const addExistingPlayer = (player: ExistingSportsPlayer) => {
    const nextPlayer = playerFromExistingProfile(player);
    setPayload((current) => {
      const players = current.players || [];
      const alreadyAdded = players.some(
        (item) =>
          (nextPlayer.registeredPlayerProfileId && item.registeredPlayerProfileId === nextPlayer.registeredPlayerProfileId) ||
          (nextPlayer.phone && item.phone && normalizeSportsPhone(item.phone) === normalizeSportsPhone(nextPlayer.phone || '')),
      );
      if (alreadyAdded) return current;
      const blankIndex = players.findIndex((item) => !item.playerName.trim() && !item.phone?.trim());
      const nextPlayers =
        blankIndex >= 0
          ? players.map((item, index) => (index === blankIndex ? nextPlayer : item))
          : [...players, nextPlayer];
      return { ...current, players: nextPlayers };
    });
    setPlayerSearchMessage(`${player.name} added to the roster.`);
  };

  const searchExistingPlayers = async () => {
    setPlayerSearchMessage('');
    if (!accountFlow.currentUser) {
      setPlayerSearchMessage('Verify or switch to a Sports Connect account before searching existing players.');
      return;
    }
    const q = playerSearch.q.trim();
    const phone = playerSearch.phone.trim();
    if (!q && !phone) {
      setPlayerSearchMessage('Enter a player name or mobile number to search.');
      return;
    }
    setPlayerSearchLoading(true);
    try {
      const results = await sportsService.searchPlayers({
        q: q || undefined,
        phone: phone || undefined,
        sport: payload.team.sport,
      });
      setPlayerSearchResults(results);
      setPlayerSearchMessage(results.length ? '' : 'No existing sports player found. Add this player manually below.');
    } catch (error: any) {
      setPlayerSearchMessage(resolveApiErrorMessage(error, 'Unable to search existing players.'));
    } finally {
      setPlayerSearchLoading(false);
    }
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (teamSubmitted) return;
    if (!captainComplete || !teamComplete) {
      const targetStep = !captainComplete ? 1 : 2;
      setStep(targetStep);
      setMessage({ type: 'error', text: getStepLockMessage(targetStep + 1) || 'Complete required team details before submitting.' });
      return;
    }
    setSubmitting(true);
    setMessage(null);
    try {
      const cleanPlayers = (payload.players || []).filter((player) => player.playerName.trim());
      const submitPayload: CreateTeamPayload = {
        ...payload,
        captain: {
          ...payload.captain,
          mobileNumber: captainContactPhone,
        },
      };
      const result = await accountFlow.submitWithSportsAccount(async () => {
        await sportsService.createTeam({ ...submitPayload, players: cleanPlayers });
      });
      if (result === 'switch_prompt') return;
      if (result !== 'otp_sent') {
        setTeamSubmitted(true);
      }
      setMessage({
        type: 'success',
        text:
          result === 'otp_sent'
            ? 'Account OTP sent. Enter OTP and submit again to create the Sports Connect team.'
            : 'Team submitted for approval. Players can verify their profiles later.',
      });
    } catch (error: any) {
      setMessage({ type: 'error', text: resolveApiErrorMessage(error, 'Unable to create team.') });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell
      title="Create Team"
      description="Create your team now. Players can verify their profiles later."
      path="/sports/create-team"
      schema={makeSportsPageSchemas(
        'Create Team',
        'Create your team now. Players can verify their profiles later.',
        '/sports/create-team',
        'Local sports team registration',
      )}
    >
      <section className={sectionClass('max-w-6xl')}>
        <form onSubmit={submit} className={panelClass('space-y-6')}>
          <Message state={message} />
          <SportsAccountPanel flow={accountFlow} title="Create Sports Captain Account" />
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {['Captain', 'Team', 'Players', 'Submit'].map((label, index) => {
              const targetStep = index + 1;
              const locked = Boolean(getStepLockMessage(targetStep));
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => goToStep(targetStep)}
                  className={`min-h-[48px] rounded-lg px-3 py-3 text-sm font-semibold ${
                    step === targetStep
                      ? 'bg-slate-950 text-white'
                      : locked
                        ? 'cursor-not-allowed bg-slate-100 text-slate-400'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {targetStep}. {label}
                </button>
              );
            })}
          </div>

          {step === 1 && (
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Captain Name" value={payload.captain.name} required onChange={(value) => updateCaptain({ name: value })} />
              <Field label="Age" type="number" value={payload.captain.age ? String(payload.captain.age) : ''} onChange={(value) => updateCaptain({ age: parseNumber(value) })} />
              <Field label="Alternate Phone" value={payload.captain.alternatePhone || ''} onChange={(value) => updateCaptain({ alternatePhone: value })} />
              <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm leading-6 text-slate-700 md:col-span-2">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Captain contact</p>
                <p className="mt-1 text-base font-semibold text-slate-950">{captainContactPhone || 'Use the Sports account phone above'}</p>
                <p className="mt-1 text-xs text-slate-500">This is taken from the Sports Connect account phone, so the captain mobile is not entered twice.</p>
              </div>
              <LocationDropdownFields
                value={payload.captain.location}
                required
                className="md:col-span-2"
                onChange={(location) => setPayload((current) => ({ ...current, captain: { ...current.captain, location } }))}
              />
              <CheckboxField label="Also add captain as a player" checked={payload.captain.addAsPlayer !== false} onChange={(checked) => updateCaptain({ addAsPlayer: checked })} />
            </div>
          )}

          {step === 2 && (
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Team Name" value={payload.team.teamName} required onChange={(value) => updateTeam({ teamName: value })} />
              <SelectField label="Sport Category" value={payload.team.sport} options={sportOptions} required onChange={(value) => updateTeam({ sport: value as SportCategoryName })} />
              <LocationDropdownFields
                value={payload.team.location}
                required
                className="md:col-span-2"
                onChange={(location) => setPayload((current) => ({ ...current, team: { ...current.team, location } }))}
              />
              <Field label="Home Ground" value={payload.team.homeGround || ''} onChange={(value) => updateTeam({ homeGround: value })} />
              <SelectField label="Team Level" value={payload.team.teamLevel || 'LOCAL'} options={levelOptions} onChange={(value) => updateTeam({ teamLevel: value as ExperienceLevel })} />
              <Field label="Preferred Match Days" value={(payload.team.preferredMatchDays || []).join(', ')} onChange={(value) => updateTeam({ preferredMatchDays: value.split(',').map((item) => item.trim()).filter(Boolean) })} />
              <Field label="Preferred Match Timings" value={(payload.team.preferredMatchTimings || []).join(', ')} onChange={(value) => updateTeam({ preferredMatchTimings: value.split(',').map((item) => item.trim()).filter(Boolean) })} />
              <div className="md:col-span-2">
                <TextAreaField label="Team Description" value={payload.team.description || ''} onChange={(value) => updateTeam({ description: value })} />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm leading-6 text-emerald-900">
                Add existing Sports Connect players when possible. If no match is found, use manual entry and the player can verify later.
              </div>
              <div className="grid grid-cols-2 gap-2 rounded-lg bg-slate-100 p-1">
                {[
                  { id: 'existing' as const, label: 'Existing players' },
                  { id: 'manual' as const, label: 'Manual entry' },
                ].map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setPlayerSearchMode(item.id)}
                    className={`min-h-[44px] rounded-md px-3 text-sm font-semibold transition ${
                      playerSearchMode === item.id ? 'bg-white text-slate-950 shadow-sm' : 'text-slate-600 hover:text-slate-950'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              {playerSearchMode === 'existing' && (
                <div className="space-y-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                  <div className="grid gap-4 md:grid-cols-[1fr_1fr_auto]">
                    <Field label="Search by name" value={playerSearch.q} onChange={(value) => setPlayerSearch((current) => ({ ...current, q: value }))} />
                    <Field label="Search by mobile" value={playerSearch.phone} onChange={(value) => setPlayerSearch((current) => ({ ...current, phone: value }))} />
                    <div className="flex items-end">
                      <PrimaryButton type="button" disabled={playerSearchLoading} className="w-full" onClick={() => void searchExistingPlayers()}>
                        <Search className="h-4 w-4" /> {playerSearchLoading ? 'Checking...' : 'Find'}
                      </PrimaryButton>
                    </div>
                  </div>
                  {playerSearchMessage && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm leading-6 text-amber-900">
                      {playerSearchMessage}
                    </div>
                  )}
                  <div className="grid gap-3">
                    {playerSearchResults.map((player) => (
                      <article key={player.id} className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="min-w-0">
                          <p className="font-semibold text-slate-950">{player.name}</p>
                          <p className="mt-1 text-sm text-slate-600">
                            {[player.phone, getPlayerRoleForSport(player, payload.team.sport) || formatEnum(payload.team.sport), getSportsLocationLabel(player.location)].filter(Boolean).join(' | ')}
                          </p>
                        </div>
                        <PrimaryButton type="button" className="w-full sm:w-auto" onClick={() => addExistingPlayer(player)}>
                          <Plus className="h-4 w-4" /> Add
                        </PrimaryButton>
                      </article>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <h3 className="text-base font-semibold text-slate-950">Team roster</h3>
                  <SecondaryButton type="button" className="w-full sm:w-auto" onClick={() => setPayload((current) => ({ ...current, players: [...(current.players || []), emptyManualPlayer()] }))}>
                    <Plus className="h-4 w-4" /> Add manual player
                  </SecondaryButton>
                </div>
                {(payload.players || []).map((player, index) => (
                  <div key={index} className="grid gap-4 rounded-lg border border-slate-200 bg-slate-50 p-4 sm:p-5 md:grid-cols-6">
                    <Field label="Player Name" value={player.playerName} onChange={(value) => updatePlayer(index, { playerName: value, registeredPlayerProfileId: undefined, registeredUserId: undefined })} />
                    <Field label="Phone" value={player.phone || ''} onChange={(value) => updatePlayer(index, { phone: value, registeredPlayerProfileId: undefined, registeredUserId: undefined })} />
                    <SelectField label="Role / Position" value={player.role || ''} options={getPlayingRoleOptions(payload.team.sport)} onChange={(value) => updatePlayer(index, { role: value })} />
                    <Field label="Age" type="number" value={player.age ? String(player.age) : ''} onChange={(value) => updatePlayer(index, { age: parseNumber(value) })} />
                    <Field label="Jersey Number" value={player.jerseyNumber || ''} onChange={(value) => updatePlayer(index, { jerseyNumber: value })} />
                    <div className="flex items-end">
                      {player.registeredPlayerProfileId ? (
                        <span className="inline-flex min-h-[44px] w-full items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50 px-3 text-sm font-semibold text-emerald-800">
                          Existing player
                        </span>
                      ) : (
                        <CheckboxField label="Send invite" checked={Boolean(player.sendInvite)} onChange={(checked) => updatePlayer(index, { sendInvite: checked })} />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-3 sm:gap-4">
                <Stat title="Team" value={payload.team.teamName || 'Not set'} />
                <Stat title="Sport" value={formatEnum(payload.team.sport)} />
                <Stat title="Roster players" value={(payload.players || []).filter((player) => player.playerName.trim()).length} />
              </div>
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-950">
                Team status will be PENDING after submission. Captain status is VERIFIED from the logged-in account. Players may be VERIFIED, INVITED, or UNCLAIMED.
              </div>
            </div>
          )}

          <div className="grid gap-3 sm:flex sm:flex-wrap sm:justify-between">
            <SecondaryButton type="button" disabled={step === 1} onClick={() => setStep((current) => Math.max(1, current - 1))}>
              Back
            </SecondaryButton>
            {step < 4 ? (
              <PrimaryButton type="button" onClick={() => goToStep(Math.min(4, step + 1))}>
                Next
              </PrimaryButton>
            ) : (
              <PrimaryButton type="submit" disabled={submitting || teamSubmitted} className="w-full sm:w-auto">
                <ClipboardCheck className="h-4 w-4" /> {teamSubmitted ? 'Team Submitted' : submitting ? 'Submitting...' : 'Submit Team'}
              </PrimaryButton>
            )}
          </div>
        </form>
      </section>
    </PageShell>
  );
};

export const TeamsPage: React.FC = () => {
  const [sportFilter, setSportFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState<SportsLocationValue>(createSearchLocation());
  const [teams, setTeams] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await sportsService.nearbyTeams(compactRecord({ sport: sportFilter, ...toSportsLocationQuery(locationFilter) }));
      setTeams(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  }, [locationFilter, sportFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <PageShell
      title="Nearby Teams"
      description="Search village and local teams by sport, area, mandal and level."
      path="/sports/teams"
      schema={makeCollectionPageSchemas({
        title: 'Nearby Teams',
        description: 'Search village and local teams by sport, area, mandal and level.',
        path: '/sports/teams',
        items: teams.slice(0, 20).map((team) => ({
          name: team.teamName,
          path: `/sports/teams/${getRecordId(team)}`,
        })),
      })}
    >
      <section className={sectionClass()}>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void load();
          }}
          className={panelClass('grid gap-4 md:grid-cols-[minmax(180px,0.7fr)_minmax(0,2fr)_auto]')}
        >
          <SelectField label="Sport" value={sportFilter} options={sportOptions} onChange={setSportFilter} />
          <LocationDropdownFields value={locationFilter} onChange={setLocationFilter} />
          <div className="flex items-end">
            <PrimaryButton type="submit" disabled={loading} className="w-full">
              <Search className="h-4 w-4" /> {loading ? 'Searching...' : 'Search'}
            </PrimaryButton>
          </div>
        </form>
        <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {teams.map((team) => <TeamCard key={getRecordId(team)} team={team} />)}
        </div>
        {!teams.length && <div className="mt-8 rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600 shadow-sm sm:p-5">No teams found for this search.</div>}
      </section>
    </PageShell>
  );
};

export const TeamDetailPage: React.FC = () => {
  const { teamId = '' } = useParams();
  const { user } = useAuth();
  const currentUser: any = (user as any)?.user ? (user as any).user : user;
  const [data, setData] = useState<any>(null);
  const [message, setMessage] = useState<MessageState>(null);
  const [player, setPlayer] = useState<ManualTeamPlayerPayload>(emptyManualPlayer());

  const load = useCallback(async () => {
    if (!teamId) return;
    const result = currentUser ? await sportsService.myTeam(teamId) : await sportsService.team(teamId);
    setData(result);
  }, [currentUser, teamId]);

  useEffect(() => {
    void load().catch(() => setData(null));
  }, [load]);

  const addPlayer = async (event: React.FormEvent) => {
    event.preventDefault();
    setMessage(null);
    try {
      await sportsService.addTeamPlayers(teamId, [player]);
      setMessage({ type: 'success', text: 'Player added to roster.' });
      setPlayer(emptyManualPlayer());
      await load();
    } catch (error: any) {
      setMessage({ type: 'error', text: resolveApiErrorMessage(error, 'Unable to add player.') });
    }
  };

  const team = data?.team;
  const players = data?.players || [];
  const matches = data?.matches || [];

  return (
    <PageShell
      title={team?.teamName || 'Team Profile'}
      description="View team details, roster, fixtures and performance."
      path={`/sports/teams/${teamId}`}
      schema={team ? makeTeamDetailSchemas(team, `/sports/teams/${teamId}`, 'View team details, roster, fixtures and performance.') : undefined}
    >
      <section className={sectionClass()}>
        {team ? (
          <>
            <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4">
              <Stat title="Sport" value={formatEnum(team.sport)} />
              <Stat title="Level" value={formatEnum(team.teamLevel)} />
              <Stat title="Status" value={formatEnum(team.status)} />
              <Stat title="Record" value={`${team.stats?.wins || 0}-${team.stats?.losses || 0}-${team.stats?.draws || 0}`} />
            </div>
            <div className="mt-8 grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
              <section>
                <h2 className="text-2xl font-semibold text-slate-950">Players</h2>
                <div className="mt-4 overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
                  <table className="min-w-[760px] w-full text-left text-sm">
                    <thead className="bg-slate-100 text-xs uppercase text-slate-500">
                      <tr>
                        <th className="px-4 py-3">Name</th>
                        <th className="px-4 py-3">Role</th>
                        <th className="px-4 py-3">Phone</th>
                        <th className="px-4 py-3">Jersey</th>
                        <th className="px-4 py-3">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {players.map((item: any) => (
                        <tr key={getRecordId(item)} className="border-t border-slate-200">
                          <td className="px-4 py-3 font-medium text-slate-950">{item.playerName}</td>
                          <td className="px-4 py-3 text-slate-600">{item.role || 'Player'}</td>
                          <td className="px-4 py-3 text-slate-600">{item.phone || '-'}</td>
                          <td className="px-4 py-3 text-slate-600">{item.jerseyNumber || '-'}</td>
                          <td className="px-4 py-3"><StatusPill value={item.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
              <section>
                <h2 className="text-2xl font-semibold text-slate-950">Add Player</h2>
                {currentUser ? (
                  <form onSubmit={addPlayer} className="mt-4 space-y-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                    <Message state={message} />
                    <Field label="Player Name" value={player.playerName} required onChange={(value) => setPlayer((current) => ({ ...current, playerName: value }))} />
                    <Field label="Phone" value={player.phone || ''} onChange={(value) => setPlayer((current) => ({ ...current, phone: value }))} />
                    <SelectField label="Role / Position" value={player.role || ''} options={getPlayingRoleOptions(team?.sport)} onChange={(value) => setPlayer((current) => ({ ...current, role: value }))} />
                    <CheckboxField label="Send invite" checked={Boolean(player.sendInvite)} onChange={(checked) => setPlayer((current) => ({ ...current, sendInvite: checked }))} />
                    <PrimaryButton type="submit" className="w-full"><Plus className="h-4 w-4" /> Add Player</PrimaryButton>
                  </form>
                ) : (
                  <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm leading-6 text-emerald-900 sm:p-5">
                    Open Sports Registration to create or manage a captain-led team.
                  </div>
                )}
              </section>
            </div>
            <section className="mt-10">
              <h2 className="text-2xl font-semibold text-slate-950">Matches</h2>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                {matches.map((match: any) => (
                  <article key={getRecordId(match)} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="font-semibold text-slate-950">{match.round || formatEnum(match.matchType)}</h3>
                      <StatusPill value={match.scoreStatus || match.status} />
                    </div>
                    <p className="mt-2 text-sm text-slate-600">{[match.venue, match.matchDate ? new Date(match.matchDate).toLocaleDateString() : ''].filter(Boolean).join(' | ')}</p>
                    <p className="mt-3 text-sm text-slate-700">{match.scoreSummary || match.challengeNotes || 'Score not submitted.'}</p>
                  </article>
                ))}
              </div>
            </section>
          </>
        ) : (
          <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600 shadow-sm sm:p-5">Team not found or not approved yet.</div>
        )}
      </section>
    </PageShell>
  );
};

export const TournamentsPage: React.FC = () => {
  const [sportFilter, setSportFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState<SportsLocationValue>(createSearchLocation());
  const [items, setItems] = useState<any[]>([]);

  const load = useCallback(async () => {
    const data = await sportsService.tournaments(compactRecord({ sport: sportFilter, ...toSportsLocationQuery(locationFilter) }));
    setItems(Array.isArray(data) ? data : []);
  }, [locationFilter, sportFilter]);

  useEffect(() => {
    void load().catch(() => setItems([]));
  }, [load]);

  return (
    <PageShell
      title="Sports Tournaments"
      description="Find local tournaments and register matching-sport teams."
      path="/sports/tournaments"
      schema={makeCollectionPageSchemas({
        title: 'Sports Tournaments',
        description: 'Find local tournaments and register matching-sport teams.',
        path: '/sports/tournaments',
        items: items.slice(0, 20).map((tournament) => ({
          name: tournament.name,
          path: `/sports/tournaments/${getRecordId(tournament)}`,
        })),
      })}
    >
      <section className={sectionClass()}>
        <form onSubmit={(event) => { event.preventDefault(); void load(); }} className={panelClass('grid gap-4 md:grid-cols-[minmax(180px,0.7fr)_minmax(0,2fr)_auto]')}>
          <SelectField label="Sport" value={sportFilter} options={sportOptions} onChange={setSportFilter} />
          <LocationDropdownFields value={locationFilter} onChange={setLocationFilter} />
          <div className="flex items-end">
            <PrimaryButton type="submit" className="w-full"><Search className="h-4 w-4" /> Search</PrimaryButton>
          </div>
        </form>
        <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {items.map((tournament) => <TournamentCard key={getRecordId(tournament)} tournament={tournament} />)}
        </div>
        {!items.length && <div className="mt-8 rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600 shadow-sm sm:p-5">No open tournaments found.</div>}
      </section>
    </PageShell>
  );
};

export const CreateTournamentPage: React.FC = () => {
  const accountFlow = useSportsAccountFlow();
  const [payload, setPayload] = useState<CreateTournamentPayload>({
    name: '',
    sport: 'CRICKET',
    location: createLocation(),
    startDate: '',
    endDate: '',
    registrationDeadline: '',
    format: 'KNOCKOUT',
    maxTeams: 8,
    genderCategory: 'OPEN',
    verificationRequirement: 'UNVERIFIED_PLAYERS_ALLOWED',
    rules: '',
  });
  const [message, setMessage] = useState<MessageState>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!accountFlow.currentUser) return;
    setPayload((current) => ({
      ...current,
      organizerContact: current.organizerContact || accountFlow.currentUser.phone || accountFlow.currentUser.email || '',
    }));
  }, [accountFlow.currentUser]);

  const update = (patch: Partial<CreateTournamentPayload>) => setPayload((current) => ({ ...current, ...patch }));

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setMessage(null);
    try {
      const result = await accountFlow.submitWithSportsAccount(async () => {
        await sportsService.createTournament(payload);
      });
      if (result === 'switch_prompt') return;
      setMessage({
        type: 'success',
        text:
          result === 'otp_sent'
            ? 'Account OTP sent. Enter OTP and submit again to create the Sports Connect tournament.'
            : 'Tournament submitted. Admin can approve it for registration.',
      });
    } catch (error: any) {
      setMessage({ type: 'error', text: resolveApiErrorMessage(error, 'Unable to create tournament.') });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell
      title="Create Tournament"
      description="Organizer tournament creation with sport matching, verification rules, entry fee and fixtures."
      path="/sports/tournaments/create"
      schema={makeSportsPageSchemas(
        'Create Tournament',
        'Organizer tournament creation with sport matching, verification rules, entry fee and fixtures.',
        '/sports/tournaments/create',
        'Local sports tournament organizer registration',
      )}
    >
      <section className={sectionClass('max-w-6xl')}>
        <form onSubmit={submit} className={panelClass('space-y-5')}>
          <Message state={message} />
          <SportsAccountPanel flow={accountFlow} title="Create Sports Organizer Account" />
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Tournament Name" value={payload.name} required onChange={(value) => update({ name: value })} />
            <SelectField label="Sport Category" value={payload.sport} options={sportOptions} required onChange={(value) => update({ sport: value as SportCategoryName })} />
            <LocationDropdownFields
              value={payload.location}
              required
              className="md:col-span-2"
              onChange={(location) => update({ location })}
            />
            <Field label="Ground / Venue" value={payload.groundName || ''} onChange={(value) => update({ groundName: value })} />
            <Field label="Start Date" type="date" value={payload.startDate} required onChange={(value) => update({ startDate: value })} />
            <Field label="End Date" type="date" value={payload.endDate} required onChange={(value) => update({ endDate: value })} />
            <Field label="Registration Deadline" type="date" value={payload.registrationDeadline} required onChange={(value) => update({ registrationDeadline: value })} />
            <SelectField label="Tournament Format" value={payload.format || 'KNOCKOUT'} options={tournamentFormatOptions} onChange={(value) => update({ format: value as TournamentFormat })} />
            <Field label="Entry Fee" type="number" value={payload.entryFee ? String(payload.entryFee) : ''} onChange={(value) => update({ entryFee: parseNumber(value) })} />
            <Field label="Maximum Teams" type="number" value={String(payload.maxTeams)} required onChange={(value) => update({ maxTeams: parseNumber(value) || 2 })} />
            <Field label="Required Players Per Team" type="number" value={payload.minPlayersPerTeam ? String(payload.minPlayersPerTeam) : ''} onChange={(value) => update({ minPlayersPerTeam: parseNumber(value) })} />
            <SelectField label="Gender Category" value={payload.genderCategory || 'OPEN'} options={tournamentGenderOptions} onChange={(value) => update({ genderCategory: value as GenderCategory })} />
            <SelectField label="Verification Requirement" value={payload.verificationRequirement || 'UNVERIFIED_PLAYERS_ALLOWED'} options={verificationOptions} onChange={(value) => update({ verificationRequirement: value as VerificationRequirement })} />
            <Field label="Minimum Verified Players" type="number" value={payload.minimumVerifiedPlayers ? String(payload.minimumVerifiedPlayers) : ''} onChange={(value) => update({ minimumVerifiedPlayers: parseNumber(value) })} />
            <Field label="Organizer Contact" value={payload.organizerContact || ''} onChange={(value) => update({ organizerContact: value })} />
            <Field label="Prize Details" value={payload.prizeDetails || ''} onChange={(value) => update({ prizeDetails: value })} />
          </div>
          <TextAreaField label="Rules" value={payload.rules} required onChange={(value) => update({ rules: value })} />
          <PrimaryButton type="submit" disabled={submitting} className="w-full sm:w-auto">
            <Trophy className="h-4 w-4" /> {submitting ? 'Submitting...' : 'Create Tournament'}
          </PrimaryButton>
        </form>
      </section>
    </PageShell>
  );
};

export const TournamentDetailPage: React.FC = () => {
  const { tournamentId = '' } = useParams();
  const { user } = useAuth();
  const currentUser: any = (user as any)?.user ? (user as any).user : user;
  const [data, setData] = useState<any>(null);
  const [myTeams, setMyTeams] = useState<any[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [message, setMessage] = useState<MessageState>(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!tournamentId) return;
    const [tournamentData, teamsData] = await Promise.all([
      sportsService.tournament(tournamentId),
      currentUser ? sportsService.myTeams().catch(() => []) : Promise.resolve([]),
    ]);
    setData(tournamentData);
    setMyTeams(Array.isArray(teamsData) ? teamsData : []);
  }, [currentUser, tournamentId]);

  useEffect(() => {
    void load().catch(() => setData(null));
  }, [load]);

  const register = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setMessage(null);
    try {
      await sportsService.registerTeamForTournament(tournamentId, { teamId: selectedTeamId });
      setMessage({ type: 'success', text: 'Team registration submitted for organizer approval.' });
      await load();
    } catch (error: any) {
      setMessage({ type: 'error', text: resolveApiErrorMessage(error, 'Unable to register team.') });
    } finally {
      setSubmitting(false);
    }
  };

  const generateFixtures = async () => {
    setMessage(null);
    try {
      await sportsService.generateFixtures(tournamentId);
      setMessage({ type: 'success', text: 'Fixtures generated from approved team registrations.' });
      await load();
    } catch (error: any) {
      setMessage({ type: 'error', text: resolveApiErrorMessage(error, 'Unable to generate fixtures.') });
    }
  };

  const tournament = data?.tournament;
  const eligibleTeams = useMemo(() => myTeams.filter((team) => team.sport === tournament?.sport), [myTeams, tournament?.sport]);

  return (
    <PageShell
      title={tournament?.name || 'Tournament'}
      description="Only teams matching this sport can register for this tournament."
      path={`/sports/tournaments/${tournamentId}`}
      schema={
        tournament
          ? makeTournamentDetailSchemas(tournament, `/sports/tournaments/${tournamentId}`, 'Only teams matching this sport can register for this tournament.')
          : undefined
      }
    >
      <section className={sectionClass()}>
        {tournament ? (
          <>
            <Message state={message} />
            <div className="mt-4 grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4">
              <Stat title="Sport" value={formatEnum(tournament.sport)} />
              <Stat title="Format" value={formatEnum(tournament.format)} />
              <Stat title="Status" value={formatEnum(tournament.status)} />
              <Stat title="Entry fee" value={typeof tournament.entryFee === 'number' ? `INR ${tournament.entryFee}` : 'Free'} />
            </div>
            <div className="mt-8 grid gap-6 lg:grid-cols-[0.85fr_1.15fr]">
              {currentUser ? (
                <form onSubmit={register} className="space-y-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                  <h2 className="text-xl font-semibold text-slate-950">Register Team</h2>
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm leading-6 text-emerald-900">
                    Please check rules, entry fee, and verification requirements before submitting.
                  </div>
                  <SelectField label="Eligible team" value={selectedTeamId} options={eligibleTeams.map((team) => getRecordId(team))} required onChange={setSelectedTeamId} />
                  <PrimaryButton type="submit" disabled={submitting || !selectedTeamId} className="w-full">
                    <CheckCircle2 className="h-4 w-4" /> {submitting ? 'Submitting...' : 'Register Team'}
                  </PrimaryButton>
                </form>
              ) : (
                <div className="space-y-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4 sm:p-5">
                  <h2 className="text-xl font-semibold text-emerald-950">Sports registration required</h2>
                  <p className="text-sm leading-6 text-emerald-900">
                    Create a Sports Connect team first, then return here to register for this matching-sport tournament.
                  </p>
                  <Link to="/sports/register" className="inline-flex min-h-[48px] w-full items-center justify-center rounded-lg bg-slate-950 px-5 py-3 text-sm font-semibold text-white sm:w-auto">
                    Open Sports Registration
                  </Link>
                </div>
              )}
              <section>
                <h2 className="text-xl font-semibold text-slate-950">Fixtures</h2>
                <div className="mt-4 grid gap-4">
                  {(data.fixtures || []).map((fixture: any) => (
                    <article key={getRecordId(fixture)} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                      <div className="flex items-center justify-between gap-3">
                        <h3 className="font-semibold text-slate-950">{fixture.round || 'Match'}</h3>
                        <StatusPill value={fixture.scoreStatus} />
                      </div>
                      <p className="mt-2 text-sm text-slate-600">{[fixture.venue, fixture.matchDate ? new Date(fixture.matchDate).toLocaleDateString() : ''].filter(Boolean).join(' | ')}</p>
                      <p className="mt-3 text-sm text-slate-700">{fixture.scoreSummary || 'Score not submitted.'}</p>
                    </article>
                  ))}
                  {!data.fixtures?.length && <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600 shadow-sm sm:p-5">No fixtures yet.</div>}
                </div>
                <SecondaryButton type="button" onClick={() => void generateFixtures()} className="mt-4">
                  <CalendarDays className="h-4 w-4" /> Generate Fixtures
                </SecondaryButton>
              </section>
            </div>
            <section className="mt-10 rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
              <h2 className="text-xl font-semibold text-slate-950">Rules</h2>
              <p className="mt-3 whitespace-pre-line text-sm leading-6 text-slate-700">{tournament.rules}</p>
            </section>
          </>
        ) : (
          <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600 shadow-sm sm:p-5">Tournament not found or not open yet.</div>
        )}
      </section>
    </PageShell>
  );
};

export const ChallengeMatchPage: React.FC = () => {
  const { user } = useAuth();
  const currentUser: any = (user as any)?.user ? (user as any).user : user;
  const [myTeams, setMyTeams] = useState<any[]>([]);
  const [teams, setTeams] = useState<any[]>([]);
  const [payload, setPayload] = useState<CreateChallengePayload>({
    sport: 'CRICKET',
    teamAId: '',
    teamBId: '',
    matchDate: '',
    matchType: 'FRIENDLY',
  });
  const [message, setMessage] = useState<MessageState>(null);

  useEffect(() => {
    if (currentUser) {
      sportsService.myTeams().then((data) => setMyTeams(Array.isArray(data) ? data : [])).catch(() => setMyTeams([]));
    }
    sportsService.teams().then((data) => setTeams(Array.isArray(data) ? data : [])).catch(() => setTeams([]));
  }, [currentUser]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setMessage(null);
    try {
      await sportsService.createChallenge(payload);
      setMessage({ type: 'success', text: 'Match challenge sent.' });
    } catch (error: any) {
      setMessage({ type: 'error', text: resolveApiErrorMessage(error, 'Unable to send challenge.') });
    }
  };

  return (
    <PageShell title="Challenge Match" description="Send a local friendly, practice or warmup match request to another team." path="/sports/challenge-match" robots="noindex, nofollow">
      <section className={sectionClass('max-w-5xl')}>
        {currentUser ? (
          <form onSubmit={submit} className={panelClass('space-y-5')}>
            <Message state={message} />
            <div className="grid gap-4 md:grid-cols-2">
              <SelectField label="Sport" value={payload.sport} options={sportOptions} required onChange={(value) => setPayload((current) => ({ ...current, sport: value as SportCategoryName }))} />
              <SelectField label="Your Team" value={payload.teamAId} options={myTeams.map((team) => getRecordId(team))} required onChange={(value) => setPayload((current) => ({ ...current, teamAId: value }))} />
              <SelectField label="Opponent Team" value={payload.teamBId} options={teams.filter((team) => team.sport === payload.sport).map((team) => getRecordId(team))} required onChange={(value) => setPayload((current) => ({ ...current, teamBId: value }))} />
              <Field label="Match Date" type="date" value={payload.matchDate} required onChange={(value) => setPayload((current) => ({ ...current, matchDate: value }))} />
              <Field label="Match Time" type="time" value={payload.matchTime || ''} onChange={(value) => setPayload((current) => ({ ...current, matchTime: value }))} />
              <Field label="Venue" value={payload.venue || ''} onChange={(value) => setPayload((current) => ({ ...current, venue: value }))} />
              <Field label="Overs / Sets / Duration" value={payload.oversSetsOrDuration || ''} onChange={(value) => setPayload((current) => ({ ...current, oversSetsOrDuration: value }))} />
              <Field label="Entry contribution" type="number" value={payload.entryContribution ? String(payload.entryContribution) : ''} onChange={(value) => setPayload((current) => ({ ...current, entryContribution: parseNumber(value) }))} />
            </div>
            <TextAreaField label="Rules" value={payload.rules || ''} onChange={(value) => setPayload((current) => ({ ...current, rules: value }))} />
            <PrimaryButton type="submit" className="w-full sm:w-auto"><Target className="h-4 w-4" /> Send Challenge</PrimaryButton>
          </form>
        ) : (
          <div className="space-y-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4 sm:p-5">
            <h2 className="text-xl font-semibold text-emerald-950">Create a Sports Connect team first</h2>
            <p className="text-sm leading-6 text-emerald-900">Match challenges are available after captain/team registration.</p>
            <Link to="/sports/create-team" className="inline-flex min-h-[48px] w-full items-center justify-center rounded-lg bg-slate-950 px-5 py-3 text-sm font-semibold text-white sm:w-auto">
              Create Team
            </Link>
          </div>
        )}
      </section>
    </PageShell>
  );
};

export const SportsProfilePage: React.FC = () => {
  const { user } = useAuth();
  const currentUser: any = (user as any)?.user ? (user as any).user : user;
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    if (!currentUser) return;
    sportsService.myPlayerProfile().then(setData).catch(() => setData(null));
  }, [currentUser]);

  if (!currentUser) {
    return <SportsRegistrationPage />;
  }

  return (
    <PageShell title="My Sports Profile" description="View player profiles, teams, claims and performance history." path="/sports/profile" robots="noindex, nofollow">
      <section className={sectionClass()}>
        <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4">
          <Stat title="Sports profiles" value={data?.sportProfiles?.length || 0} />
          <Stat title="Teams" value={data?.teams?.length || 0} />
          <Stat title="Roster links" value={data?.teamPlayers?.length || 0} />
          <Stat title="Performances" value={data?.performances?.length || 0} />
        </div>
        <div className="mt-8 grid gap-6 lg:grid-cols-2">
          <section>
            <h2 className="text-2xl font-semibold text-slate-950">Sport Profiles</h2>
            <div className="mt-4 grid gap-4">
              {(data?.sportProfiles || []).map((profile: any) => (
                <article key={getRecordId(profile)} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                  <h3 className="text-lg font-semibold text-slate-950">{formatEnum(profile.sport)}</h3>
                  <p className="mt-2 text-sm text-slate-600">{profile.role} | {formatEnum(profile.experienceLevel)}</p>
                  <div className="mt-4 flex flex-wrap gap-2 text-xs font-semibold">
                    <span className="rounded-full bg-emerald-50 px-3 py-1 text-emerald-700">{profile.stats?.matchesPlayed || 0} matches</span>
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">{profile.stats?.wins || 0} wins</span>
                    <span className="rounded-full bg-amber-50 px-3 py-1 text-amber-700">{profile.stats?.awardsCount || 0} awards</span>
                  </div>
                </article>
              ))}
            </div>
          </section>
          <section>
            <h2 className="text-2xl font-semibold text-slate-950">My Teams</h2>
            <div className="mt-4 grid gap-4">
              {(data?.teams || []).map((team: any) => <TeamCard key={getRecordId(team)} team={team} />)}
            </div>
          </section>
        </div>
      </section>
    </PageShell>
  );
};

export const ClaimProfilePage: React.FC = () => {
  const accountFlow = useSportsAccountFlow();
  const [payload, setPayload] = useState({ teamPlayerId: '', phone: '', playerName: '', requestedRole: '' });
  const [message, setMessage] = useState<MessageState>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setMessage(null);
    try {
      const result = await accountFlow.submitWithSportsAccount(async () => {
        await sportsService.claimProfile(Object.fromEntries(Object.entries(payload).filter(([, value]) => value)));
      });
      if (result === 'switch_prompt') return;
      setMessage({
        type: 'success',
        text:
          result === 'otp_sent'
            ? 'Account OTP sent. Enter OTP and submit again to claim this Sports Connect profile.'
            : 'Player profile claimed and linked to your account.',
      });
    } catch (error: any) {
      setMessage({ type: 'error', text: resolveApiErrorMessage(error, 'Unable to claim profile.') });
    }
  };

  return (
    <PageShell title="Claim Player Profile" description="This player profile was added by your team captain. Verify your mobile number to claim your sports profile and maintain your performance history." path="/sports/claim-profile" robots="noindex, nofollow">
      <section className={sectionClass('max-w-3xl')}>
        <form onSubmit={submit} className={panelClass('space-y-5')}>
          <Message state={message} />
          <SportsAccountPanel flow={accountFlow} title="Create Sports Player Account" />
          <Field label="Team Player ID" value={payload.teamPlayerId} onChange={(value) => setPayload((current) => ({ ...current, teamPlayerId: value }))} />
          <Field label="Phone Number" value={payload.phone} onChange={(value) => setPayload((current) => ({ ...current, phone: value }))} />
          <Field label="Player Name" value={payload.playerName} onChange={(value) => setPayload((current) => ({ ...current, playerName: value }))} />
          <Field label="Requested Role" value={payload.requestedRole} onChange={(value) => setPayload((current) => ({ ...current, requestedRole: value }))} />
          <PrimaryButton type="submit" className="w-full sm:w-auto"><ShieldCheck className="h-4 w-4" /> Claim Profile</PrimaryButton>
        </form>
      </section>
    </PageShell>
  );
};

export const GroundsPage: React.FC = () => {
  const accountFlow = useSportsAccountFlow();
  const [grounds, setGrounds] = useState<any[]>([]);
  const [payload, setPayload] = useState<CreateGroundPayload>({
    groundName: '',
    location: createLocation(),
    supportedSports: ['CRICKET'],
    contactNumber: '',
    lightsAvailable: false,
    waterAvailable: false,
    parkingAvailable: false,
    washroomAvailable: false,
  });
  const [message, setMessage] = useState<MessageState>(null);

  const load = useCallback(async () => {
    const data = await sportsService.grounds();
    setGrounds(Array.isArray(data) ? data : []);
  }, []);

  useEffect(() => {
    void load().catch(() => setGrounds([]));
  }, [load]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setMessage(null);
    try {
      const result = await accountFlow.submitWithSportsAccount(async () => {
        await sportsService.createGround(payload);
      });
      if (result === 'switch_prompt') return;
      setMessage({
        type: 'success',
        text:
          result === 'otp_sent'
            ? 'Account OTP sent. Enter OTP and submit again to list this Sports Connect ground.'
            : 'Ground submitted for approval.',
      });
      await load();
    } catch (error: any) {
      setMessage({ type: 'error', text: resolveApiErrorMessage(error, 'Unable to submit ground.') });
    }
  };

  return (
    <PageShell
      title="Sports Grounds"
      description="Find and list local grounds for friendly matches, tournaments and practice."
      path="/sports/grounds"
      schema={makeCollectionPageSchemas({
        title: 'Sports Grounds',
        description: 'Find and list local grounds for friendly matches, tournaments and practice.',
        path: '/sports/grounds',
        items: grounds.slice(0, 20).map((ground) => ({
          name: ground.groundName,
          path: '/sports/grounds',
        })),
      })}
    >
      <section className={sectionClass('max-w-7xl', 'grid gap-6 lg:grid-cols-[1.1fr_0.9fr]')}>
        <div>
          <h2 className="text-2xl font-semibold text-slate-950">Approved Grounds</h2>
          <div className="mt-4 grid gap-4">
            {grounds.map((ground) => (
              <article key={getRecordId(ground)} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <h3 className="text-lg font-semibold text-slate-950">{ground.groundName}</h3>
                    <p className="mt-1 text-sm text-slate-600">{[ground.location?.area, ground.location?.district].filter(Boolean).join(' | ')}</p>
                  </div>
                  <StatusPill value={ground.status} />
                </div>
                <div className="mt-4 flex flex-wrap gap-2 text-xs font-semibold">
                  {(ground.supportedSports || []).map((sport: string) => <span key={sport} className="rounded-full bg-emerald-50 px-3 py-1 text-emerald-700">{formatEnum(sport)}</span>)}
                  {ground.lightsAvailable && <span className="rounded-full bg-amber-50 px-3 py-1 text-amber-700">Lights</span>}
                  {ground.parkingAvailable && <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">Parking</span>}
                </div>
              </article>
            ))}
          </div>
        </div>
        <form onSubmit={submit} className={panelClass('space-y-4')}>
          <h2 className="text-xl font-semibold text-slate-950">List Ground</h2>
          <Message state={message} />
          <SportsAccountPanel flow={accountFlow} title="Create Sports Ground Owner Account" />
          <Field label="Ground Name" value={payload.groundName} required onChange={(value) => setPayload((current) => ({ ...current, groundName: value }))} />
          <LocationDropdownFields
            value={payload.location}
            required
            onChange={(location) => setPayload((current) => ({ ...current, location }))}
          />
          <Field label="Contact Number" value={payload.contactNumber} required onChange={(value) => setPayload((current) => ({ ...current, contactNumber: value }))} />
          <SelectField label="Primary Sport" value={payload.supportedSports[0] || 'CRICKET'} options={sportOptions} onChange={(value) => setPayload((current) => ({ ...current, supportedSports: [value as SportCategoryName] }))} />
          <Field label="Rent per match" type="number" value={payload.rentPerMatch ? String(payload.rentPerMatch) : ''} onChange={(value) => setPayload((current) => ({ ...current, rentPerMatch: parseNumber(value) }))} />
          <div className="grid gap-3 sm:grid-cols-2">
            <CheckboxField label="Lights" checked={Boolean(payload.lightsAvailable)} onChange={(checked) => setPayload((current) => ({ ...current, lightsAvailable: checked }))} />
            <CheckboxField label="Water" checked={Boolean(payload.waterAvailable)} onChange={(checked) => setPayload((current) => ({ ...current, waterAvailable: checked }))} />
            <CheckboxField label="Parking" checked={Boolean(payload.parkingAvailable)} onChange={(checked) => setPayload((current) => ({ ...current, parkingAvailable: checked }))} />
            <CheckboxField label="Washroom" checked={Boolean(payload.washroomAvailable)} onChange={(checked) => setPayload((current) => ({ ...current, washroomAvailable: checked }))} />
          </div>
          <PrimaryButton type="submit" className="w-full"><Building2 className="h-4 w-4" /> Submit Ground</PrimaryButton>
        </form>
      </section>
    </PageShell>
  );
};

export const AdminSportsPage: React.FC = () => {
  const [overview, setOverview] = useState<any>(null);
  const [teams, setTeams] = useState<any[]>([]);
  const [tournaments, setTournaments] = useState<any[]>([]);
  const [matches, setMatches] = useState<any[]>([]);
  const [message, setMessage] = useState<MessageState>(null);

  const load = useCallback(async () => {
    const [overviewData, teamsData, tournamentsData, matchesData] = await Promise.all([
      sportsService.adminOverview(),
      sportsService.adminTeams(),
      sportsService.adminTournaments(),
      sportsService.adminMatches(),
    ]);
    setOverview(overviewData);
    setTeams(Array.isArray(teamsData) ? teamsData : []);
    setTournaments(Array.isArray(tournamentsData) ? tournamentsData : []);
    setMatches(Array.isArray(matchesData) ? matchesData : []);
  }, []);

  useEffect(() => {
    void load().catch(() => undefined);
  }, [load]);

  const action = async (work: Promise<any>, success: string) => {
    setMessage(null);
    try {
      await work;
      setMessage({ type: 'success', text: success });
      await load();
    } catch (error: any) {
      setMessage({ type: 'error', text: resolveApiErrorMessage(error, 'Admin action failed.') });
    }
  };

  const pendingTeams = teams.filter((team) => team.status === 'PENDING');
  const draftTournaments = tournaments.filter((tournament) => tournament.status === 'DRAFT');
  const scoreReviews = matches.filter((match) => ['SUBMITTED', 'OPPONENT_CONFIRMED'].includes(match.scoreStatus));

  return (
    <PageShell title="Admin Sports" description="Approve teams and tournaments, monitor matches, verify scores and review Sports Connect growth." path="/admin/sports" robots="noindex, nofollow">
      <section className={sectionClass()}>
        <Message state={message} />
        <div className="mt-4 grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4">
          {Object.entries(overview || {}).map(([key, value]) => <Stat key={key} title={formatEnum(key)} value={String(value)} />)}
        </div>

        <h2 className="mt-10 text-2xl font-semibold text-slate-950">Pending Team Approvals</h2>
        <div className="mt-4 grid gap-4">
          {pendingTeams.map((team) => (
            <article key={getRecordId(team)} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-slate-950">{team.teamName}</h3>
                  <p className="mt-1 text-sm text-slate-600">{[formatEnum(team.sport), team.location?.area, team.homeGround].filter(Boolean).join(' | ')}</p>
                </div>
                <div className="grid gap-2 sm:flex">
                  <PrimaryButton type="button" onClick={() => void action(sportsService.approveTeam(getRecordId(team)), 'Team approved.')}>Approve</PrimaryButton>
                  <SecondaryButton type="button" onClick={() => void action(sportsService.rejectTeam(getRecordId(team)), 'Team rejected.')}>Reject</SecondaryButton>
                </div>
              </div>
            </article>
          ))}
        </div>

        <h2 className="mt-10 text-2xl font-semibold text-slate-950">Tournament Approvals</h2>
        <div className="mt-4 grid gap-4">
          {draftTournaments.map((tournament) => (
            <article key={getRecordId(tournament)} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-slate-950">{tournament.name}</h3>
                  <p className="mt-1 text-sm text-slate-600">{[formatEnum(tournament.sport), tournament.location?.area, formatEnum(tournament.format)].filter(Boolean).join(' | ')}</p>
                </div>
                <div className="grid gap-2 sm:flex">
                  <PrimaryButton type="button" onClick={() => void action(sportsService.approveTournament(getRecordId(tournament)), 'Tournament approved.')}>Approve</PrimaryButton>
                  <SecondaryButton type="button" onClick={() => void action(sportsService.rejectTournament(getRecordId(tournament)), 'Tournament rejected.')}>Reject</SecondaryButton>
                </div>
              </div>
            </article>
          ))}
        </div>

        <h2 className="mt-10 text-2xl font-semibold text-slate-950">Score Verification</h2>
        <div className="mt-4 grid gap-4">
          {scoreReviews.map((match) => (
            <article key={getRecordId(match)} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-slate-950">{formatEnum(match.sport)} Match</h3>
                  <p className="mt-1 text-sm text-slate-600">{match.scoreSummary || 'Score submitted'}</p>
                </div>
                <PrimaryButton type="button" onClick={() => void action(sportsService.verifyScore(getRecordId(match)), 'Score verified and stats updated.')}>
                  Verify Score
                </PrimaryButton>
              </div>
            </article>
          ))}
        </div>
      </section>
    </PageShell>
  );
};

export const SponsorsPage: React.FC = () => {
  const [items, setItems] = useState<any[]>([]);
  useEffect(() => {
    sportsService.sponsors().then((data) => setItems(Array.isArray(data) ? data : [])).catch(() => setItems([]));
  }, []);
  return (
    <PageShell
      title="Sports Sponsors"
      description="Local businesses can support tournaments with banners, prizes and featured sponsor listings."
      path="/sports/sponsors"
      schema={makeCollectionPageSchemas({
        title: 'Sports Sponsors',
        description: 'Local businesses can support tournaments with banners, prizes and featured sponsor listings.',
        path: '/sports/sponsors',
        items: items.slice(0, 20).map((sponsor) => ({
          name: sponsor.sponsorName,
          path: '/sports/sponsors',
        })),
      })}
    >
      <section className={sectionClass()}>
        <div className="grid gap-4 md:grid-cols-3">
          {items.map((sponsor) => (
            <article key={getRecordId(sponsor)} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
              <Handshake className="h-6 w-6 text-emerald-700" />
              <h2 className="mt-3 text-lg font-semibold text-slate-950">{sponsor.sponsorName}</h2>
              <p className="mt-2 text-sm text-slate-600">{[sponsor.businessType, formatEnum(sponsor.sponsorshipType)].filter(Boolean).join(' | ')}</p>
              <StatusPill value={sponsor.status} />
            </article>
          ))}
        </div>
      </section>
    </PageShell>
  );
};
